const CUE_CONFIGURATOR_LABEL_STYLES = new Set(['none', 'italic', 'bold', 'both']);
const getConfiguratorComponentUnavailableLabel = (el) => {
  const variantTitle = (el?.variant?.title ?? '').trim();
  const shortened = (el.dataset.selectedProductShortenedTitle || '').trim();
  if (shortened) return variantTitle ? `${shortened} - ${variantTitle}` : shortened;  // If variant title is available, add it to the shortened title
  const title = (el.dataset.selectedProductTitle || '').trim();
  if (title) return variantTitle ? `${title} - ${variantTitle}` : title;  // If variant title is available, add it to the title
  const type = (el.dataset.componentType || '').trim();
  if (type){
    let returnValue = `Cue ${type.toLowerCase()}`;
    if(variantTitle){
      returnValue += ` - ${variantTitle}`;
    }
    return returnValue;
  }
  return '';  // If no title is available, return an empty string
}

/** Same rules as `updateAvailabilityAndButton` / `updateUnavailableMessage`. */
const isConfiguratorComponentPriceUnavailable = (el) => {
  if (!el) return false;
  return (
    el.dataset.available === 'false' ||
    (el.dataset.selectedVariantId &&
      el.dataset.selectedVariantId !== '' &&
      el.dataset.available !== 'true')
  );
};

/** `[data-component-price]` nodes in the same `.product-form` that are currently unavailable (live DOM). */
const getUnavailableConfiguratorComponentsInForm = (contextEl) => {
  const scope = contextEl?.closest?.('.product-form') || document;
  return [...scope.querySelectorAll('[data-component-price]')].filter((el) =>
    isConfiguratorComponentPriceUnavailable(el)
  );
};

/**
 * Globo “back in stock” only works for the main product row (`base-picker-options`), not add-on `cue-builder-product` lines.
 * @param {Element|null} el
 * @returns {boolean}
 */
const isGloboBackInStockConfiguratorComponent = (el) => {
  return el?.matches?.('base-picker-options') === true;
};

/**
 * Cart / API titles sometimes include HTML entities (e.g. &quot;). Use before textContent so quotes render correctly.
 * @param {string|null|undefined} raw
 * @returns {string}
 */
const decodeHtmlEntities = (raw) => {
  if (raw == null || raw === '') return '';
  const ta = document.createElement('textarea');
  ta.innerHTML = String(raw);
  return ta.value;
}

const strTrimOr = (value, fallback) => {
  const s = value != null ? String(value).trim() : '';
  return s || fallback;
};

/** Space–en dash–space between the product label and the Globo control in verbose list `<li>` rows. */
const GLOBO_LIST_ITEM_WIDGET_SEPARATOR = ' \u2013 ';

const capitalizeFirstLetter = (s) => {
  const t = (s || '').trim();
  if (!t) return t;
  return t.charAt(0).toLocaleUpperCase() + t.slice(1);
};

const normalizeCueConfiguratorLabelStyle = (raw) => {
  const s = raw != null ? String(raw).trim().toLowerCase() : '';
  if (CUE_CONFIGURATOR_LABEL_STYLES.has(s)) return s;
  return 'italic';
}

/** @returns {'verbose' | 'simplified'} */
const normalizeCueMultiMessageMode = (raw) => {
  const s = raw != null ? String(raw).trim().toLowerCase() : '';
  return s === 'simplified' ? 'simplified' : 'verbose';
}

/**
 * Shared styled name for unavailable [label] and cart error product titles / [name].
 * @param {string} text
 * @param {string} style none | italic | bold | both
 */
const createCueConfiguratorLabelElement = (text, style, titlesAreLinks = null, productLink = null) => {
  const decoded = decodeHtmlEntities(text);
  const st = normalizeCueConfiguratorLabelStyle(style);
  const cls = 'custom-cue-configurator-msg-label';
  let link = null;
  if(titlesAreLinks && productLink) {
    link = document.createElement('a');
    link.classList.add(cls);
    link.href = productLink;
    link.textContent = decoded;
  }
  if (st === 'none') {

    const span = document.createElement('span');
    span.classList.add(cls);
    if(link) {
      span.appendChild(link);
    } else {  
    span.textContent = decoded;
    }
    return span;
  }
  if (st === 'bold') {
    const strong = document.createElement('strong');
    strong.classList.add(cls);
    if(link) {
      strong.appendChild(link);
    } else {
      strong.textContent = decoded;
    }
    return strong;
  }
  if (st === 'both') {
    const strong = document.createElement('strong');
    strong.classList.add(cls);
    const em = document.createElement('em');
    if(link) {
      em.appendChild(link);
    } else {
      em.textContent = decoded;
    }
    strong.appendChild(em);
    return strong;
  }
  const em = document.createElement('em');
  em.classList.add(cls);
  if(link) {
    em.appendChild(link);
  } else {
    em.textContent = decoded;
  }
  return em;
}

const ensureChild = (parent, selector, tagName, ...classNames) => {
  if (!parent) return null;
  let el = parent.querySelector(selector);
  if (!el) {
    el = document.createElement(tagName);
    classNames.forEach((c) => {
      if (c) el.classList.add(c);
    });
    parent.appendChild(el);
  }
  return el;
}

const resetCustomCueErrorDynamicContent = (container) => {
  if (!container) return;
  container
    .querySelectorAll(
      '.custom-cue-error-msg-body, .custom-cue-error-msg-products-list, .custom-cue-error-msg-header-title'
    )
    .forEach((el) => el.remove());
}

const resetCustomCueUnavailableMsgContent = (container) => {
  if (!container) return;
  container
    .querySelectorAll(
      '.custom-cue-unavailable-msg-header-title, .custom-cue-unavailable-msg-body, .custom-cue-unavailable-msg-globo, .custom-cue-unavailable-msg-list'
    )
    .forEach((el) => el.remove());
}

// Global tooltip functionality
(function initTooltips() {
  let tooltipButtons = new Set();
  let documentClickHandler = null;

  function getStickyViewport() {
    return document.querySelector('.tooltip__viewport');
  }

  function returnContentToButton(content) {
    const owner = content._tooltipOwnerButton;
    if (owner) {
      owner.appendChild(content);
      content.setAttribute('hidden', '');
      content.style.top = '';
      content._tooltipOwnerButton = null;
    } else {
      content.setAttribute('hidden', '');
    }
  }

  function closeAllTooltips() {
    const viewport = getStickyViewport();
    if (viewport) {
      viewport.querySelectorAll('.tooltip__content').forEach(returnContentToButton);
      viewport.setAttribute('hidden', '');
    }
    tooltipButtons.forEach((btn) => {
      const content = btn.querySelector('.tooltip__content');
      if (content && !content.hasAttribute('hidden')) {
        content.setAttribute('hidden', '');
      }
    });
  }

  function openTooltipInSticky(button, tooltipContent) {
    console.log('openTooltipInSticky',button,tooltipContent);
    const viewport = getStickyViewport();
    if (!viewport) {
      tooltipContent.toggleAttribute('hidden');
      return;
    }
    closeAllTooltips();
    tooltipContent.removeAttribute('hidden');
    tooltipContent._tooltipOwnerButton = button;
    button.removeChild(tooltipContent);
    viewport.appendChild(tooltipContent);
    viewport.removeAttribute('hidden');

    const stickyBar = viewport.closest('configurator-sticky-price-atc');
    if (stickyBar) {
      const stickyRect = stickyBar.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      const topPx = buttonRect.top - stickyRect.bottom;
      tooltipContent.style.top = `${topPx + 8}px`;
    }
  }

  function closeTooltipInSticky(tooltipContent) {
    const viewport = getStickyViewport();
    if (viewport && viewport.contains(tooltipContent)) {
      returnContentToButton(tooltipContent);
      viewport.setAttribute('hidden', '');
    } else {
      tooltipContent.setAttribute('hidden', '');
    }
  }

  function setupTooltip(button) {
    if (tooltipButtons.has(button)) return;
    tooltipButtons.add(button);

    const handler = (e) => {
      e.preventDefault();
      if (e.target.closest('.tooltip__content')) return;
      let tooltipContent = button.querySelector('.tooltip__content');
      const viewport = getStickyViewport();
      if (!tooltipContent && viewport && button.dataset.tooltipTarget) {
        tooltipContent = viewport.querySelector(`.tooltip__content[data-tooltip-handle="${button.dataset.tooltipTarget}"]`);
      }
      if (!tooltipContent) return;

      const isInViewport = viewport && viewport.contains(tooltipContent);

      if (isInViewport || (!isInViewport && !tooltipContent.hasAttribute('hidden'))) {
        closeTooltipInSticky(tooltipContent);
      } else {
        openTooltipInSticky(button, tooltipContent);
      }
    };
    button.addEventListener('click', handler);
  }

  // Set up document click handler once
  if (!documentClickHandler) {
    documentClickHandler = (e) => {
      // Check if click is inside any tooltip content
      const clickedTooltipContent = e.target.closest('.tooltip__content');
      if (clickedTooltipContent) {
        return; // Don't close if clicking inside tooltip content
      }

      // Check if click is on a tooltip button
      const clickedTooltipButton = e.target.closest('.tooltip');
      if (clickedTooltipButton) {
        return; // Button click handler will handle this
      }

      // Close all open tooltips
      closeAllTooltips();
    };
    document.addEventListener('click', documentClickHandler);
  }

  // Close tooltips on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const viewport = getStickyViewport();
    const hasOpenInViewport = viewport && !viewport.hasAttribute('hidden');
    const hasOpenInline = Array.from(tooltipButtons).some((btn) => {
      const content = btn.querySelector('.tooltip__content');
      return content && !content.hasAttribute('hidden');
    });
    if (hasOpenInViewport || hasOpenInline) {
      e.preventDefault();
      closeAllTooltips();
    }
  });

  function init() {
    // Set up existing tooltips
    document.querySelectorAll('.tooltip').forEach(setupTooltip);

    // Watch for dynamically added tooltips
    if (document.body) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) { // Element node
              // Check if the added node is a tooltip
              if (node.matches && node.matches('.tooltip')) {
                setupTooltip(node);
              }
              // Check for tooltips within the added node
              const tooltips = node.querySelectorAll?.('.tooltip');
              if (tooltips) {
                tooltips.forEach(setupTooltip);
              }
            }
          });
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

const GLOBO_BACK_IN_STOCK_ID = 'Globo-Back-In-Stock';
const GLOBO_ORIGINAL_LABEL_ATTR = 'data-globo-original-label';
/** Hidden container on `configurator-sticky-price-atc` so `#Globo-Back-In-Stock` stays in `document` while unavailable copy is rebuilt. */
const GLOBO_UNAVAILABLE_PARK_CLASS = 'custom-cue-globo-unavailable-park';
/** Suffix in each multi-OOS verbose list item; holds Globo (real + optional clones) inline after the product label. */
const GLOBO_LI_SUFFIX_CLASS = 'custom-cue-globo-li-suffix';
/** Cloned Globo root for 2+ list rows (no duplicate `#Globo-Back-In-Stock` id). */
const GLOBO_BIS_CLONE_CLASS = 'custom-cue-globo-bis-clone';

const findConfiguratorComponentByVariantIdInForm = (productForm, variantId) => {
  if (!productForm || variantId == null) return null;
  const v = String(variantId).trim();
  if (!v) return null;
  for (const el of productForm.querySelectorAll('[data-component-price]')) {
    if ((el.dataset?.selectedVariantId || '').trim() === v) return el;
  }
  return null;
};

/** Clone roots cannot reuse `#Globo-Back-In-Stock`; strip only the root id (leave inner nodes for Globo scripts). */
const stripGloboCloneRootId = (root) => {
  if (root) root.removeAttribute('id');
};

let globoBackInStockDocumentObserver = null;
let globoVariantChangeListenerAddedForGlobo = false;
/** >0 while we reparent Globo so the document observer does not re-enter sync (would freeze the page). */
let globoDocumentObserverSuppressed = 0;

const globoNodeTouchesGlobo = (node) => {
  if (!node || node.nodeType !== 1) return false;
  if (node.id === GLOBO_BACK_IN_STOCK_ID) return true;
  return !!(node.querySelector && node.querySelector(`#${GLOBO_BACK_IN_STOCK_ID}`));
};

const globoMutationsMentionGlobo = (mutations) => {
  for (let i = 0; i < mutations.length; i++) {
    const m = mutations[i];
    if (m.type !== 'childList') continue;
    for (let j = 0; j < m.addedNodes.length; j++) {
      if (globoNodeTouchesGlobo(m.addedNodes[j])) return true;
    }
    for (let k = 0; k < m.removedNodes.length; k++) {
      if (globoNodeTouchesGlobo(m.removedNodes[k])) return true;
    }
  }
  return false;
};

let globoStickySyncDebounceTimer = null;

/** Calls `scheduleGloboSync()` on the canonical sticky (override host if present). Debounced so reparenting Globo does not re-trigger the document MutationObserver in a tight loop. */
const scheduleGloboSyncOnConfiguratorSticky = () => {
  if (globoStickySyncDebounceTimer != null) {
    clearTimeout(globoStickySyncDebounceTimer);
  }
  globoStickySyncDebounceTimer = setTimeout(() => {
    globoStickySyncDebounceTimer = null;
    const sticky =
      document.querySelector('configurator-sticky-price-atc[data-globo-override]') ||
      document.querySelector('configurator-sticky-price-atc');
    sticky?.scheduleGloboSync?.();
  }, 40);
};

const initGloboBackInStockDocumentObserverOnce = () => {
  if (typeof MutationObserver !== 'undefined' && !globoBackInStockDocumentObserver) {
    globoBackInStockDocumentObserver = new MutationObserver((mutations) => {
      if (globoDocumentObserverSuppressed > 0) return;
      if (!globoMutationsMentionGlobo(mutations)) return;
      scheduleGloboSyncOnConfiguratorSticky();
    });
    globoBackInStockDocumentObserver.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }
  if (!globoVariantChangeListenerAddedForGlobo) {
    globoVariantChangeListenerAddedForGlobo = true;
    document.addEventListener('on:variant:change', scheduleGloboSyncOnConfiguratorSticky);
  }
};

const isSafeGloboButtonHexColor = (value) => {
  return /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test((value || '').trim());
};

/**
 * Klarna's own on-site-messaging script reconciles a placement's purchase amount back to
 * the base variant price shortly after we set it (a one-time correction per trigger, not a
 * continuous watch), so a plain attribute write gets silently overwritten. Track the desired
 * amount per placement and use a MutationObserver to reassert it whenever Klarna's script
 * changes it back - confirmed empirically to converge (no fight loop) since Klarna doesn't
 * react a second time to our own corrective write.
 */
const klarnaPlacementSync = new WeakMap();

/**
 * Updates the Klarna on-site-messaging placement's purchase amount and asks Klarna to
 * refresh it. `amount` is in major currency units (e.g. dollars) - Klarna wants minor units.
 */
const updateKlarnaPlacement = (amount) => {
  const placement = document.querySelector('klarna-placement');

  if (!placement) return;

  const desired = String(Math.round(amount * 100));
  placement.dataset.purchaseAmount = desired;

  const sync = klarnaPlacementSync.get(placement);
  if (sync) {
    sync.desired = desired;
  } else {
    const state = { desired };
    const observer = new MutationObserver(() => {
      if (placement.dataset.purchaseAmount !== state.desired) {
        placement.dataset.purchaseAmount = state.desired;
      }
    });
    observer.observe(placement, { attributes: true, attributeFilter: ['data-purchase-amount'] });
    klarnaPlacementSync.set(placement, state);
  }

  if (window.Klarna?.OnsiteMessaging) {
    window.Klarna.OnsiteMessaging.refresh();
  }

  correctKlarnaMonthlyAmount(amount);
};

// Klarna's own 12-payment installment plan, reverse-engineered from its "More options"
// breakdown (12 payments, $107.65/mo, $92.79 total interest on a real $1,199 purchase):
// $92.79 / $1,199 ~= 7.74% simple annual interest, split evenly over the term. Independently
// confirmed against the badge's own (uncorrected) figure for a $650 purchase: $650 * 1.0774 /
// 12 ~= $58.36, rounding to the $58/mo it actually displayed.
const KLARNA_INSTALLMENT_TERM_MONTHS = 12;
const KLARNA_INSTALLMENT_APR = 92.79 / 1199;

const klarnaMonthlySync = new WeakMap();

/**
 * Klarna's on-site messaging badge computes its "$X/month" figure from the base variant
 * price and re-derives it from its own internal source regardless of data-purchase-amount
 * (confirmed: no supported client-side hook makes it reflect the configurator's combined
 * total). As a stopgap, recompute the figure ourselves using Klarna's own installment plan
 * math (see above) applied to the real combined total, and write it into the badge's
 * osm-message element in place, reapplying whenever Klarna (re)renders that message.
 *
 * `amount` can be stale by the time the shadow root is actually ready (e.g. an early call
 * during initial component hydration, superseded by a later call once totals settle), so the
 * desired value lives in shared, mutable state per placement rather than a closure - every
 * retry/observer callback re-reads it fresh instead of applying whatever amount it started with.
 */
const correctKlarnaMonthlyAmount = (amount) => {
  const placement = document.querySelector('klarna-placement');
  if (!placement) return;

  const desired = (
    (amount * (1 + KLARNA_INSTALLMENT_APR)) / KLARNA_INSTALLMENT_TERM_MONTHS
  ).toFixed(2);

  let state = klarnaMonthlySync.get(placement);
  if (state) {
    state.desired = desired;
  } else {
    state = { desired };
    klarnaMonthlySync.set(placement, state);
  }

  applyKlarnaMonthlyAmount(placement, state);
};

const applyKlarnaMonthlyAmount = (placement, state, attemptsLeft = 20) => {
  const shadow = placement.shadowRoot;

  // Klarna attaches its shadow root asynchronously after its own script loads/hydrates, so on
  // a fresh page load it may not exist yet - retry for a few seconds rather than giving up.
  if (!shadow) {
    if (attemptsLeft > 0) {
      setTimeout(() => applyKlarnaMonthlyAmount(placement, state, attemptsLeft - 1), 250);
    }
    return;
  }

  const apply = () => {
    const strong = shadow.querySelector('.osm-message strong');
    if (!strong) return;

    const match = strong.textContent.match(/\$([\d,]+(?:\.\d+)?)\/month/);
    if (!match) return;

    const shown = match[1].replace(/,/g, '');
    if (shown === state.desired) return;

    strong.textContent = strong.textContent.replace(match[0], `$${state.desired}/month`);
  };

  apply();

  if (!placement.dataset.monthlyCorrectorAttached) {
    placement.dataset.monthlyCorrectorAttached = 'true';
    new MutationObserver(apply).observe(shadow, { childList: true, subtree: true, characterData: true });
  }
};

/**
 * Updates Shopify's native shopify-payment-terms (Shop Pay Installments) widget to reflect
 * the configurator's combined total. Unlike klarna-placement, this is a first-party, reactive
 * custom element - shopify-meta is one of its observedAttributes - and it recomputes the
 * displayed monthly price itself client-side from `full_price` plus the financing_plans table
 * already embedded in shopify-meta (confirmed: changing only full_price and leaving the old
 * price_per_term in place still produced the correct new monthly amount), so there's no
 * interest/APR math to replicate here and no reconciliation to fight.
 */
const updateShopifyPaymentTerms = (amount) => {
  const el = document.querySelector('shopify-payment-terms');
  if (!el) return;

  const metaRaw = el.getAttribute('shopify-meta');
  if (!metaRaw) return;

  let meta;
  try {
    meta = JSON.parse(metaRaw);
  } catch (e) {
    return;
  }

  const variantId = Number(el.getAttribute('variant-id'));
  const variantEntry = meta.variants?.find((v) => v.id === variantId);
  if (!variantEntry) return;

  variantEntry.full_price = new Intl.NumberFormat(window.Shopify?.locale || 'en-US', {
    style: 'currency',
    currency: meta.currency_code || window.Shopify?.currency?.active || 'USD',
  }).format(amount);

  el.setAttribute('shopify-meta', JSON.stringify(meta));
};

if(!customElements.get('configurator-sticky-price-atc')){
  class ConfiguratorStickyPriceAtc extends HTMLElement {

    connectedCallback() {
      // Listen for events from cue-builder-product and base-picker-options
      this.componentUpdateHandler = this.componentUpdateHandler.bind(this);
      this.updateUnavailableCount();
      this.messageCloseButtons = this.querySelectorAll('.custom-message__wrapper .custom-message__close');
      this.messageCloseButtons.forEach((closeBtn) => {
        closeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.closeMessage(closeBtn.closest('.custom-message__wrapper'));
        });
      });
      document.addEventListener('cue-component-update', this.componentUpdateHandler);
      this.showCurrencyCode = this.dataset.showCurrencyCode;
      this.componentPrices = Object.fromEntries(
        Array.from(
          this.components).map((item) => {
            return [item.dataset.selectedVariantId, item.dataset.componentPrice]
          }
        )
      );
      console.log(this.componentPrices)
      this.totalPrice = this.calculateTotalPrice(this.componentPrices);
      this.renderCombinedPrice(this.componentPrices);
      // Initial availability/button state and form variant id after components have set data-available
      setTimeout(() => {
        this.updateAvailabilityAndButton();
        this.updateFormVariantInputs();
        // klarna-placement lives in the product info column, further down the document than
        // this element, and may not exist yet when renderCombinedPrice ran synchronously above -
        // retry once the initial parse has settled so the page-load amount is accurate too.
        updateKlarnaPlacement(this.totalPrice / 100);
      }, 0);
      initGloboBackInStockDocumentObserverOnce();
    }
    
    disconnectedCallback() {
      document.removeEventListener('cue-component-update', this.componentUpdateHandler);
    }
    updateUnavailableCount() {
      this.components = document.querySelectorAll('[data-component-price]');
      this.unavailableCount = Array.from(this.components).filter((el) =>
        isConfiguratorComponentPriceUnavailable(el)
      ).length;
    }
    updateComponentPrices() {
      const priceMap = Array.from(this.components).map((item) => {
        return [item.dataset.selectedVariantId, item.dataset.componentPrice]
      });
      return Object.fromEntries(priceMap.filter((item) => item[0] !== ''));
    }
    closeMessage(message) {
      message.hidden = true;
    }
    componentUpdateHandler(evt) {
      // Update this element based on the event detail
      const { componentSelectedVariant } = evt.detail;
      this.components = document.querySelectorAll('[data-component-price]');
      const newComponentPrices = this.updateComponentPrices();
      const newTotalPrice = this.calculateTotalPrice(newComponentPrices);
      if(newTotalPrice !== this.totalPrice){
        this.totalPrice = newTotalPrice;
        const formattedPrice = this.renderCombinedPrice(newComponentPrices);
        theme.announcePrice(this, formattedPrice);
      }
      this.updateAvailabilityAndButton();
      this.updateFormVariantInputs();
      this.updateUnavailableCount();
    }

    /**
     * Syncs the sticky form's hidden variant id/sku inputs from base-picker-options
     * so the submitted variant matches the selected base (butt/shaft) variant.
     */
    updateFormVariantInputs() {
      const form = this.querySelector('form');
      if (!form) return;
      const productForm = this.closest('.product-form');
      const basePicker = productForm?.querySelector('base-picker-options');
      if (!basePicker) return;
      const idInput = form.querySelector('input[name="id"]');
      const skuInput = form.querySelector('input[name="sku"]');
      if (idInput && basePicker.dataset.selectedVariantId !== undefined) {
        idInput.value = basePicker.dataset.selectedVariantId || '';
      }
      if (skuInput && basePicker.dataset.selectedVariantSku !== undefined) {
        skuInput.value = basePicker.dataset.selectedVariantSku || '';
      }
    }

    /**
     * Early access state for the selected base variant, mirroring early_access_disabled in
     * blocks/_buy-buttons.liquid: blank/'all' -> enabled, 'none' -> disabled, otherwise enabled
     * only if the customer has a matching tag. Returns null when there's nothing to gate on.
     */
    getEarlyAccessState() {
      const script = this.querySelector('[data-cue-early-access]');
      if (!script) return null;
      if (!this.earlyAccessData) {
        try {
          this.earlyAccessData = JSON.parse(script.textContent);
        } catch (e) {
          return null;
        }
      }

      const basePicker = this.closest('.product-form')?.querySelector('base-picker-options');
      const variantId = basePicker?.dataset.selectedVariantId
        || this.querySelector('form input[name="id"]')?.value;
      if (!variantId) return null;

      const entry = this.earlyAccessData.variants[String(variantId)] || {};
      const tiers = entry.tiers || [];
      const customerTags = this.earlyAccessData.customerTags || [];
      let disabled;
      if (!tiers.length || tiers.includes('all')) disabled = false;
      else if (tiers.includes('none')) disabled = true;
      else disabled = !tiers.some((tag) => customerTags.includes(tag));

      return { disabled, label: entry.label || this.earlyAccessData.comingSoonText };
    }

    /**
     * Updates add-to-cart button and availability UI based on all configurator components.
     * Any component with a selected variant (non-empty data-selected-variant-id) must be available.
     */
    updateAvailabilityAndButton() {
      const components = document.querySelectorAll('[data-component-price]');
      let allAvailable = true;
      components.forEach((el) => {
        if (el.dataset.available === 'false') {
          allAvailable = false;
        } else if (el.dataset.selectedVariantId && el.dataset.selectedVariantId !== '' && el.dataset.available !== 'true') {
          allAvailable = false;
        }
      });

      const submitBtn = this.querySelector('.add-to-cart');
      if (submitBtn) {
        const earlyAccess = this.getEarlyAccessState();
        // Product-level VIP gate (data-upcoming-drop) is fixed server-side; per-variant early
        // access follows the selected base variant.
        if (earlyAccess && submitBtn.dataset.upcomingDrop !== 'true') {
          submitBtn.dataset.vipDisabled = earlyAccess.disabled ? 'true' : 'false';
          if (earlyAccess.disabled) submitBtn.textContent = earlyAccess.label;
        }
        const vipDisabled = submitBtn.dataset.vipDisabled === 'true';
        const comingSoon = submitBtn.dataset.comingSoon === 'true';
        submitBtn.disabled = !allAvailable || vipDisabled || comingSoon;
        submitBtn.setAttribute('aria-disabled', (!allAvailable || vipDisabled || comingSoon) ? 'true' : 'false');
        if (!vipDisabled) {
          const addText = this.dataset.addToCartText || '';
          const noStockText = this.dataset.noStockText || '';
          submitBtn.textContent = allAvailable ? addText : noStockText;
        }
      }

      const availabilityStatus = this.querySelector('[data-configurator-availability-status]');
      if (availabilityStatus) {
        const inStockText = this.dataset.inStockText || '';
        const noStockText = this.dataset.noStockText || '';
        availabilityStatus.textContent = allAvailable ? inStockText : noStockText;
      }

      const availabilityBlock = this.querySelector('[data-configurator-availability]');
      if (availabilityBlock) {
        availabilityBlock.classList.toggle('is-unavailable', !allAvailable);
        availabilityBlock.classList.toggle('product-inventory--low', !allAvailable);
        availabilityBlock.classList.toggle('product-inventory--ok', allAvailable);
      }

      if (!allAvailable) {
        const productForm = this.closest('custom-cue-product-form');
        if (productForm && typeof productForm.setCustomErrorMsgState === 'function') {
          productForm.setCustomErrorMsgState(false);
        }
      }

      this.updateUnavailableMessage(allAvailable, components);
    }

    /**
     * Shows the sticky "unavailable" callout when any configurator component is out of stock
     * (same conditions as the sold-out add-to-cart state). Hides and clears it when everything is available.
     */
    updateUnavailableMessage(allAvailable, components) {
      const root = this.querySelector('.custom-cue-unavailable-msg');
      if (!root) return;

      // Cancel any pending banner reveal scheduled from a previous call before applying new visibility state
      if (this.pendingRevealId) {
        cancelAnimationFrame(this.pendingRevealId);
        this.pendingRevealId = null;
      }

      if (allAvailable) {
        root.hidden = true;
        const innerClear = root.querySelector('.custom-cue-unavailable-msg-inner');
        if (innerClear) {
          this.#parkGloboBackInStockNodeIfInInner(innerClear);
          resetCustomCueUnavailableMsgContent(innerClear);
        }
        this.scheduleGloboSync();
        return;
      }

      const inner = root.querySelector('.custom-cue-unavailable-msg-inner');
      if (!inner) return;

      /** Keep Globo in `document` while we replace body/list markup (Globo does not re-inject on child-only updates). */
      this.#parkGloboBackInStockNodeIfInInner(inner);

      /** Expose after layout so WebKit/VoiceOver sees the full subtree (role=alert). */
      const showUnavailableRoot = () => {
        const reveal = () => {
          this.pendingRevealId = null;
          root.hidden = false;
          // Globo sync reads `.custom-cue-unavailable-msg` visibility; it was scheduled synchronously
          // below while `root.hidden` could still be true. Re-run after reveal so reparent / button
          // copy always see the visible callout (fixes flaky Globo when toggling OOS quickly).
          this.scheduleGloboSync();
        };
        if (typeof requestAnimationFrame === 'function') {
          this.pendingRevealId = requestAnimationFrame(reveal);
        } else {
          reveal();
        }
      };

      const labels = [];
      const labelSourceEls = [];
      const componentLinks = [];
      const seen = new Set();
      components.forEach((el) => {
        const unavailable =
          el.dataset.available === 'false' ||
          (el.dataset.selectedVariantId &&
            el.dataset.selectedVariantId !== '' &&
            el.dataset.available !== 'true');
        if (!unavailable) return;
        const label =
          getConfiguratorComponentUnavailableLabel(el) ||
          strTrimOr(this.dataset.cueUnavailableFallbackLabel, 'Selected option');
        if (!seen.has(label)) {
          seen.add(label);
          labels.push(label);
          labelSourceEls.push(el);
          componentLinks.push(
            el.dataset.selectedProductHandle
              ? `/products/${el.dataset.selectedProductHandle}?variant=${encodeURIComponent(String(el.dataset.selectedVariantId || ''))}`
              : null
          );
        }
      });

      const labelStyle = normalizeCueConfiguratorLabelStyle(this.dataset.cueUnavailableLabelStyle);

      const header = inner.querySelector('.custom-cue-unavailable-msg-header');
      if (header) {
        const titleSpan = ensureChild(
          header,
          '.custom-cue-unavailable-msg-header-title',
          'span',
          'custom-cue-unavailable-msg-header-title'
        );
        titleSpan.textContent = strTrimOr(
          this.dataset.cueUnavailableHeader,
          'This configuration is not available!'
        );
      }

      if (labels.length === 0) {
        inner.querySelector('.custom-cue-unavailable-msg-list')?.remove();
        const body = ensureChild(
          inner,
          '.custom-cue-unavailable-msg-body',
          'p',
          'custom-cue-unavailable-msg-body'
        );
        body.textContent = strTrimOr(
          this.dataset.cueUnavailableBodyNoLabels,
          'Your selected configuration includes an item that is out of stock. Select a different option to continue.'
        );
      } else if (labels.length === 1) {
        inner.querySelector('.custom-cue-unavailable-msg-list')?.remove();
        const body = ensureChild(
          inner,
          '.custom-cue-unavailable-msg-body',
          'p',
          'custom-cue-unavailable-msg-body'
        );
        const template = strTrimOr(
          this.dataset.cueUnavailableBodySingle,
          '[label] is currently out of stock. Select a different option to continue.'
        );
        const ph = '[label]';
        const phIdx = template.indexOf(ph);
        body.replaceChildren();
        const labelEl = createCueConfiguratorLabelElement(
          labels[0],
          labelStyle,
          this.dataset.cueUnavailableLinkTitles === 'true',
          componentLinks[0]
        );
        if (phIdx === -1) {
          body.append(labelEl, document.createTextNode(` ${template}`));
        } else {
          body.append(
            document.createTextNode(template.slice(0, phIdx)),
            labelEl,
            document.createTextNode(template.slice(phIdx + ph.length))
          );
        }
      } else {
        const multiMode = normalizeCueMultiMessageMode(this.dataset.cueUnavailableBodyMultiMode);
        if (multiMode === 'simplified') {
          inner.querySelector('.custom-cue-unavailable-msg-list')?.remove();
          const body = ensureChild(
            inner,
            '.custom-cue-unavailable-msg-body',
            'p',
            'custom-cue-unavailable-msg-body'
          );
          body.textContent = strTrimOr(
            this.dataset.cueUnavailableBodyMultiSimplified,
            'Several of your selected options are out of stock. Please change your selection to continue.'
          );
        } else {
          const body = ensureChild(
            inner,
            '.custom-cue-unavailable-msg-body',
            'p',
            'custom-cue-unavailable-msg-body'
          );
          body.textContent = strTrimOr(
            this.dataset.cueUnavailableBodyMultiIntroVerbose,
            'The following options are currently out of stock:'
          );
          const list = ensureChild(
            inner,
            '.custom-cue-unavailable-msg-list',
            'ul',
            'custom-cue-unavailable-msg-list'
          );
          list.replaceChildren();
          labels.forEach((label, index) => {
            const item = document.createElement('li');
            item.classList.add('custom-cue-unavailable-msg-list-item');
            const src = labelSourceEls[index];
            const vid = (src?.dataset?.selectedVariantId || '').trim();
            if (vid && isGloboBackInStockConfiguratorComponent(src)) {
              item.dataset.cueBisUnavailVariant = vid;
            }
            item.appendChild(
              createCueConfiguratorLabelElement(
                label,
                labelStyle,
                this.dataset.cueUnavailableLinkTitles === 'true',
                componentLinks[index]
              )
            );
            if (isGloboBackInStockConfiguratorComponent(src)) {
              const globoSuffix = document.createElement('span');
              globoSuffix.className = GLOBO_LI_SUFFIX_CLASS;
              item.appendChild(globoSuffix);
            }
            list.appendChild(item);
          });
        }
      }

      showUnavailableRoot();
      // Reparent / restyle Globo only after message markup is final (avoids `textContent` / `replaceChildren` wiping it).
      this.scheduleGloboSync();
    }
    calculateTotalPrice(prices) {
      return Object.values(prices).reduce((acc, price) => acc + Number(price), 0);
    }
    renderCombinedPrice(prices) {
      const totalPrice = Object.values(prices).reduce((acc, price) => acc + Number(price), 0);
      const formattedPrice = this.showCurrencyCode === 'true' ? theme.settings.moneyWithCurrencyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (totalPrice / 100).toFixed(2)) : theme.settings.moneyWithoutCurrencyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (totalPrice / 100).toFixed(2));
      this.querySelector('.price__current .js-value').textContent = formattedPrice;
      updateKlarnaPlacement(totalPrice / 100);
      updateShopifyPaymentTerms(totalPrice / 100);
      return formattedPrice;
    }

    /** Reparent / restyle Globo “back in stock” when the widget or variant state changes. */
    scheduleGloboSync() {
      this.#scheduleGloboBackInStockReparent();
    }

    #getBasePickerOptions() {
      return this.closest('.product-form')?.querySelector('base-picker-options') ?? null;
    }

    /** True when the main (parent) product row is treated as out of stock for configurator rules. */
    #isParentConfiguratorUnavailable() {
      return isConfiguratorComponentPriceUnavailable(this.#getBasePickerOptions());
    }

    /** Detaches `#Globo-Back-In-Stock` from visible unavailable markup into a hidden node on this sticky. */
    #getOrCreateGloboUnavailableParkEl() {
      let park = this.querySelector(`.${GLOBO_UNAVAILABLE_PARK_CLASS}`);
      if (!park) {
        park = document.createElement('div');
        park.className = GLOBO_UNAVAILABLE_PARK_CLASS;
        park.hidden = true;
        park.setAttribute('aria-hidden', 'true');
        this.appendChild(park);
      }
      return park;
    }

    /**
     * If Globo lives under `inner`, moves it to the sticky park so `textContent` / `replaceChildren` cannot drop it from `document`.
     * @param {HTMLElement|null} inner - `.custom-cue-unavailable-msg-inner`
     */
    #parkGloboBackInStockNodeIfInInner(inner) {
      if (!inner) return;
      inner.querySelectorAll(`.${GLOBO_BIS_CLONE_CLASS}`).forEach((n) => n.remove());
      const g = document.getElementById(GLOBO_BACK_IN_STOCK_ID);
      if (!g || !inner.contains(g)) return;
      const row = g.closest('.custom-cue-unavailable-msg-globo');
      this.#getOrCreateGloboUnavailableParkEl().appendChild(g);
      if (row?.classList?.contains('custom-cue-unavailable-msg-globo') && row.childElementCount === 0) {
        row.remove();
      }
    }

    #getConfiguratorStickyWithGloboOverride() {
      return document.querySelector('configurator-sticky-price-atc[data-globo-override]');
    }

    #getGloboBackInStockUnavailableHost() {
      const sticky =
        this.#getConfiguratorStickyWithGloboOverride() || this;
      const bodySel = '.custom-cue-unavailable-msg-inner .custom-cue-unavailable-msg-body';
      if (sticky?.matches?.('configurator-sticky-price-atc')) {
        return (
          sticky.querySelector(bodySel) ||
          sticky.querySelector('.custom-cue-unavailable-msg-inner') ||
          sticky.querySelector('.custom-cue-unavailable-msg')
        );
      }
      return (
        document.querySelector(`configurator-sticky-price-atc ${bodySel}`) ||
        document.querySelector(bodySel)
      );
    }

    /**
     * Move Globo out of the visible unavailable message (list, intro body, or standalone row) and
     * into the hidden park (add-ons OOS only, or when Globo is not used for this state).
     */
    #parkGloboWhenIneligibleForConfiguratorUi(inner, messageBody, globoBackInStockRoot) {
      if (!globoBackInStockRoot) return;
      inner?.querySelectorAll(`.${GLOBO_BIS_CLONE_CLASS}`).forEach((n) => n.remove());
      inner?.querySelectorAll('.custom-cue-unavailable-msg-globo').forEach((p) => {
        if (p.contains(globoBackInStockRoot)) {
          globoBackInStockRoot.remove();
        }
        p.remove();
      });
      if (messageBody?.contains(globoBackInStockRoot)) {
        globoBackInStockRoot.remove();
      }
      inner?.querySelectorAll(`ul.custom-cue-unavailable-msg-list .${GLOBO_LI_SUFFIX_CLASS}`).forEach((s) => {
        s.replaceChildren();
      });
      this.#getOrCreateGloboUnavailableParkEl().appendChild(globoBackInStockRoot);
    }

    /**
     * Verbose multi-OOS (intro + `<ul>`): only `base-picker-options` rows get a list suffix; place the
     * real Globo node in the first such row. Additional parent rows (rare) get clones. Child add-on rows
     * have no suffix — Globo is not shown for them.
     */
    #distributeGloboToUnavailableListItems(inner, list, messageBody, globoBackInStockRoot) {
      inner?.querySelectorAll('.custom-cue-unavailable-msg-globo').forEach((p) => {
        if (p.contains(globoBackInStockRoot)) {
          globoBackInStockRoot.remove();
        }
        p.remove();
      });
      if (messageBody?.contains(globoBackInStockRoot)) {
        globoBackInStockRoot.remove();
      }
      const items = [...list.querySelectorAll(':scope > li.custom-cue-unavailable-msg-list-item')];
      const targets = items.filter((li) => li.querySelector(`.${GLOBO_LI_SUFFIX_CLASS}`));
      if (!targets.length) {
        this.#parkGloboWhenIneligibleForConfiguratorUi(inner, messageBody, globoBackInStockRoot);
        return true;
      }
      inner.querySelectorAll(`.${GLOBO_BIS_CLONE_CLASS}`).forEach((n) => n.remove());
      targets.forEach((li) => {
        li.querySelector(`.${GLOBO_LI_SUFFIX_CLASS}`).replaceChildren();
      });
      targets.forEach((li, i) => {
        const suffix = li.querySelector(`.${GLOBO_LI_SUFFIX_CLASS}`);
        suffix.appendChild(document.createTextNode(GLOBO_LIST_ITEM_WIDGET_SEPARATOR));
        if (i === 0) {
          suffix.appendChild(globoBackInStockRoot);
        } else {
          const c = globoBackInStockRoot.cloneNode(true);
          stripGloboCloneRootId(c);
          c.classList.add(GLOBO_BIS_CLONE_CLASS);
          suffix.appendChild(c);
        }
      });
      return true;
    }

    /**
     * When Globo lives in `.custom-cue-unavailable-msg-body`, inserts optional `data-globo-button-prefix`
     * and the Globo root before the final period (or appends at end). Button copy comes from
     * `#applyGloboStickyDatasetOverrides`. Globo is only shown for the **parent** product line
     * (`base-picker-options`); add-on OOS rows do not get the widget.
     * Prefix is only used when **one** component is unavailable (`unavailableCount <= 1`); the multi-OOS
     * path uses a separate `.custom-cue-unavailable-msg-globo` `<p>` (simplified) or in-list suffixes on
     * parent rows only (verbose; see `#distributeGloboToUnavailableListItems`).
     * @param {HTMLElement} messageBody - `.custom-cue-unavailable-msg-body`
     * @param {HTMLElement} globoBackInStockRoot - `#Globo-Back-In-Stock`
     * @returns {boolean}
     */
    #insertGloboAndPrefix(messageBody, globoBackInStockRoot) {
      const backInStockRootId = GLOBO_BACK_IN_STOCK_ID;
      if (!messageBody?.classList?.contains('custom-cue-unavailable-msg-body') || !globoBackInStockRoot) {
        return false;
      }

      const owningSticky = messageBody.closest('configurator-sticky-price-atc');
      if (typeof owningSticky?.updateUnavailableCount === 'function') {
        owningSticky.updateUnavailableCount();
      }
      // Use a live DOM count for layout (multi vs single Globo row). `unavailableCount` alone can lag
      // `cue-component-update` / `this.components` on the sticky and pick the wrong branch intermittently.
      const unavailableLive = getUnavailableConfiguratorComponentsInForm(owningSticky);

      const inner = messageBody.closest('.custom-cue-unavailable-msg-inner');

      if (unavailableLive.length > 1) {
        if (!inner) {
          return false;
        }
        const list = inner.querySelector('ul.custom-cue-unavailable-msg-list');
        if (list?.querySelector(`:scope > li .${GLOBO_LI_SUFFIX_CLASS}`)) {
          const targets = [...list.querySelectorAll(':scope > li')].filter((li) =>
            li.querySelector(`.${GLOBO_LI_SUFFIX_CLASS}`)
          );
          const suffix0 = targets[0]?.querySelector(`.${GLOBO_LI_SUFFIX_CLASS}`);
          if (
            suffix0 &&
            suffix0.contains(globoBackInStockRoot) &&
            suffix0.querySelector(`#${backInStockRootId}`) === globoBackInStockRoot
          ) {
            const nClones = list.querySelectorAll(`.${GLOBO_BIS_CLONE_CLASS}`).length;
            if (targets.length === 1 || targets.length - 1 === nClones) {
              return true;
            }
          }
          return this.#distributeGloboToUnavailableListItems(
            inner,
            list,
            messageBody,
            globoBackInStockRoot
          );
        }
        if (list) {
          this.#parkGloboWhenIneligibleForConfiguratorUi(inner, messageBody, globoBackInStockRoot);
          return true;
        }
        if (!unavailableLive.some(isGloboBackInStockConfiguratorComponent)) {
          this.#parkGloboWhenIneligibleForConfiguratorUi(inner, messageBody, globoBackInStockRoot);
          return true;
        }
        const existingGloboRow = inner.querySelector('.custom-cue-unavailable-msg-globo');
        if (
          existingGloboRow &&
          messageBody.nextElementSibling === existingGloboRow &&
          existingGloboRow.contains(globoBackInStockRoot) &&
          existingGloboRow.querySelector(`#${backInStockRootId}`) === globoBackInStockRoot
        ) {
          return true;
        }
        if (messageBody.contains(globoBackInStockRoot)) {
          globoBackInStockRoot.remove();
        }
        let globoParagraph = inner.querySelector('.custom-cue-unavailable-msg-globo');
        if (!globoParagraph) {
          globoParagraph = document.createElement('p');
          globoParagraph.className =
            'custom-cue-unavailable-msg-body custom-cue-unavailable-msg-globo';
        }
        const afterBody = messageBody.nextElementSibling;
        if (afterBody !== globoParagraph) {
          if (afterBody) {
            inner.insertBefore(globoParagraph, afterBody);
          } else {
            inner.appendChild(globoParagraph);
          }
        }
        globoParagraph.replaceChildren();
        globoParagraph.appendChild(globoBackInStockRoot);
        return true;
      }

      {
        const orphanGloboRow = inner?.querySelector('.custom-cue-unavailable-msg-globo');
        if (orphanGloboRow) {
          if (orphanGloboRow.contains(globoBackInStockRoot)) {
            globoBackInStockRoot.remove();
          }
          orphanGloboRow.remove();
        }
      }

      if (
        unavailableLive.length === 1 &&
        !isGloboBackInStockConfiguratorComponent(unavailableLive[0])
      ) {
        this.#parkGloboWhenIneligibleForConfiguratorUi(inner, messageBody, globoBackInStockRoot);
        return true;
      }

      const stickyForPrefix =
        this.#getConfiguratorStickyWithGloboOverride() || owningSticky;
      const trimmedGloboPrefix =
        stickyForPrefix?.hasAttribute('data-globo-override') &&
        (stickyForPrefix.dataset.globoButtonPrefix ?? '').trim() !== ''
          ? (stickyForPrefix.dataset.globoButtonPrefix ?? '').trim()
          : '';

      let lastTextNode = null;
      let paragraphTailText = '';
      for (let childIndex = messageBody.childNodes.length - 1; childIndex >= 0; childIndex--) {
        const child = messageBody.childNodes[childIndex];
        if (child.nodeType === Node.ELEMENT_NODE && child.id === backInStockRootId) {
          continue;
        }
        if (child.nodeType === Node.TEXT_NODE) {
          lastTextNode = child;
          paragraphTailText = child.textContent;
          break;
        }
      }

      if (!lastTextNode) {
        if (trimmedGloboPrefix !== '') {
          messageBody.appendChild(document.createTextNode(` ${trimmedGloboPrefix} `));
        }
        messageBody.appendChild(globoBackInStockRoot);
        return true;
      }

      const trailingPeriodMatch = paragraphTailText.match(/\.\s*$/);
      if (!trailingPeriodMatch) {
        if (trimmedGloboPrefix !== '') {
          messageBody.appendChild(document.createTextNode(` ${trimmedGloboPrefix} `));
        }
        messageBody.appendChild(globoBackInStockRoot);
        return true;
      }

      const textBeforeTrailingPeriod = paragraphTailText.slice(
        0,
        paragraphTailText.length - trailingPeriodMatch[0].length
      );
      const periodAndTrailingSpaces = trailingPeriodMatch[0];
      lastTextNode.textContent = textBeforeTrailingPeriod;

      const insertionFragment = document.createDocumentFragment();
      if (trimmedGloboPrefix !== '') {
        insertionFragment.appendChild(document.createTextNode(` ${trimmedGloboPrefix} `));
      }
      insertionFragment.appendChild(globoBackInStockRoot);
      insertionFragment.appendChild(document.createTextNode(periodAndTrailingSpaces));

      const nextSiblingAfterText = lastTextNode.nextSibling;
      if (nextSiblingAfterText) {
        messageBody.insertBefore(insertionFragment, nextSiblingAfterText);
      } else {
        messageBody.appendChild(insertionFragment);
      }
      return true;
    }

    #isConfiguratorComponentPriceUnavailable(el) {
      return isConfiguratorComponentPriceUnavailable(el);
    }

    /**
     * Among unavailable `[data-component-price]` nodes, prefers `base-picker-options` (main product),
     * else a `cue-builder-product` whose selected handle equals its parent handle, else the first candidate.
     * @param {ParentNode} [productFormScope] - Same scope as `#applyGloboStickyDatasetOverrides` (e.g. `sticky.closest('.product-form')`).
     * @returns {HTMLElement|null}
     */
    #findUnavailableParentConfiguratorComponent(productFormScope) {
      const productForm = productFormScope || this.closest('.product-form') || document;
      const candidates = [...productForm.querySelectorAll('[data-component-price]')].filter((el) =>
        this.#isConfiguratorComponentPriceUnavailable(el)
      );
      if (!candidates.length) return null;
      const base = productForm.querySelector('base-picker-options');
      if (base && candidates.includes(base)) return base;
      for (const el of candidates) {
        if (el.matches?.('cue-builder-product')) {
          const ph = (el.dataset.parentProductHandle || '').trim();
          const sh = (el.dataset.selectedProductHandle || '').trim();
          if (ph && sh && ph === sh) return el;
        }
      }
      return candidates[0];
    }

    /**
     * Plain display string matching `getConfiguratorComponentUnavailableLabel` (same source as
     * `custom-cue-configurator-msg-label` text for that row).
     * @param {HTMLElement|null} el
     * @returns {string}
     */
    #plainUnavailableProductLabelForConfigurator(el) {
      if (!el) return '';
      const raw =
        getConfiguratorComponentUnavailableLabel(el) ||
        strTrimOr(this.dataset.cueUnavailableFallbackLabel, 'Selected option');
      return decodeHtmlEntities(raw).trim();
    }

    /** @param {HTMLElement|null} globoRoot - `#Globo-Back-In-Stock` (list mode updates all suffix roots). */
    #applyGloboStickyDatasetOverrides(globoRoot) {
      const sticky =
        this.#getConfiguratorStickyWithGloboOverride() ||
        globoRoot?.closest('configurator-sticky-price-atc') ||
        this;
      if (!sticky?.hasAttribute('data-globo-override')) return;

      const productForm = sticky.closest('.product-form') || document;
      const inner = sticky.querySelector('.custom-cue-unavailable-msg-inner');
      const perLiSuffixes = inner
        ? [...inner.querySelectorAll(`ul.custom-cue-unavailable-msg-list li .${GLOBO_LI_SUFFIX_CLASS}`)]
        : [];
      if (perLiSuffixes.length) {
        const unavailable = getUnavailableConfiguratorComponentsInForm(sticky);
        const count = unavailable.length;
        perLiSuffixes.forEach((suffix) => {
          const root =
            suffix?.querySelector(`#${GLOBO_BACK_IN_STOCK_ID}`) ||
            suffix?.querySelector(`.${GLOBO_BIS_CLONE_CLASS}`);
          if (!root) return;
          const li = suffix.closest('li');
          const vid = (li?.dataset?.cueBisUnavailVariant || '').trim();
          const comp = vid ? findConfiguratorComponentByVariantIdInForm(productForm, vid) : null;
          this.#applyGloboStickyDatasetOverridesToRoot(sticky, root, comp, count, true);
        });
        return;
      }

      if (!globoRoot) return;
      const unavailable = getUnavailableConfiguratorComponentsInForm(sticky);
      const count = unavailable.length;
      this.#applyGloboStickyDatasetOverridesToRoot(sticky, globoRoot, null, count, false);
    }

    /**
     * @param {boolean} listItemMode - When true, button copy follows the single-OOS template using `overrideComponent` (verbose list).
     */
    #applyGloboStickyDatasetOverridesToRoot(sticky, globoRoot, overrideComponent, unavailableCount, listItemMode) {
      const attr = GLOBO_ORIGINAL_LABEL_ATTR;
      if (!sticky || !globoRoot) return;
      const btn =
        globoRoot.querySelector('button.gBackInStockBtn') ||
        globoRoot.querySelector('.gBackInStockBtn') ||
        globoRoot.querySelector('button[type="button"]');
      if (!btn) return;

      if (!btn.hasAttribute(attr)) {
        btn.setAttribute(attr, btn.textContent.trim());
      }
      const defaultLabel = btn.getAttribute(attr) || '';
      const customText = (sticky.dataset.globoButtonText || '').trim();
      const productForm = sticky.closest('.product-form') || document;
      const tmpl = strTrimOr(
        sticky.dataset.globoBisParentNotifyTemplate,
        'Get notified when [name] is back in stock.'
      );
      const fillName = (name) => tmpl.replace(/\[name\]/g, name || '');

      let label;
      if (listItemMode) {
        const name = this.#plainUnavailableProductLabelForConfigurator(overrideComponent);
        label = customText || (name ? fillName(name) : defaultLabel);
      } else if (unavailableCount > 1) {
        const parentEl = this.#findUnavailableParentConfiguratorComponent(productForm);
        const name = this.#plainUnavailableProductLabelForConfigurator(parentEl);
        label = name ? fillName(name) : customText || defaultLabel;
      } else if (unavailableCount === 1) {
        const one = getUnavailableConfiguratorComponentsInForm(sticky)[0];
        const name = this.#plainUnavailableProductLabelForConfigurator(one);
        label = customText || (name ? fillName(name) : defaultLabel);
      } else {
        label = customText || defaultLabel;
      }
      let trimmedLabel = (label || '').trim();
      if (listItemMode) {
        trimmedLabel = capitalizeFirstLetter(trimmedLabel);
      }
      if (btn.textContent.trim() !== trimmedLabel) {
        btn.textContent = trimmedLabel;
      }

      const rawColor = (sticky.dataset.globoButtonColor || '').trim();
      if (rawColor && isSafeGloboButtonHexColor(rawColor)) {
        const hex = rawColor.trim();
        if (btn.style.getPropertyValue('--globo-bis-btn-color').trim() !== hex) {
          btn.style.setProperty('--globo-bis-btn-color', hex);
        }
      } else if (btn.style.getPropertyValue('--globo-bis-btn-color')) {
        btn.style.removeProperty('--globo-bis-btn-color');
      }
    }

    #syncGloboBackInStockToUnavailableMsg() {
      globoDocumentObserverSuppressed++;
      try {
        const globoId = GLOBO_BACK_IN_STOCK_ID;
        const el = document.getElementById(globoId);
        const host = this.#getGloboBackInStockUnavailableHost();

        if (!host) {
          return;
        }

        const unavailableRoot = host.closest?.('.custom-cue-unavailable-msg');
        if (!unavailableRoot || unavailableRoot.hidden) {
          return;
        }

        if (!this.#isParentConfiguratorUnavailable()) {
          const innerFromHost = host.closest?.('.custom-cue-unavailable-msg-inner');
          if (innerFromHost) this.#parkGloboBackInStockNodeIfInInner(innerFromHost);
          return;
        }

        if (el) {
          let needReparent = el.parentNode !== host;
          if (
            needReparent &&
            host.classList?.contains('custom-cue-unavailable-msg-body')
          ) {
            const inner = host.closest('.custom-cue-unavailable-msg-inner');
            const owningSticky = host.closest('configurator-sticky-price-atc');
            if (typeof owningSticky?.updateUnavailableCount === 'function') {
              owningSticky.updateUnavailableCount();
            }
            const uc = getUnavailableConfiguratorComponentsInForm(owningSticky).length;
            if (uc > 1 && inner) {
              const globoRow = inner.querySelector('.custom-cue-unavailable-msg-globo');
              if (globoRow && el.parentNode === globoRow) {
                needReparent = false;
              }
              const listSuffix = el.closest(`.${GLOBO_LI_SUFFIX_CLASS}`);
              if (listSuffix && el.closest('ul.custom-cue-unavailable-msg-list')) {
                needReparent = false;
              }
            }
          }

          if (needReparent) {
            if (host.classList.contains('custom-cue-unavailable-msg-body')) {
              this.#insertGloboAndPrefix(host, el);
            } else {
              host.appendChild(el);
            }
          }
          this.#applyGloboStickyDatasetOverrides(el);
          requestAnimationFrame(() => {
            const g = document.getElementById(globoId);
            if (g) this.#applyGloboStickyDatasetOverrides(g);
          });
        } else {
          const inner = host.closest?.('.custom-cue-unavailable-msg-inner');
          const stale =
            inner?.querySelector(`#${globoId}`) || host.querySelector(`#${globoId}`);
          if (stale) stale.remove();
        }
      } finally {
        globoDocumentObserverSuppressed--;
      }
    }

    #scheduleGloboBackInStockReparent() {
      this.#syncGloboBackInStockToUnavailableMsg();
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => {
          this.#syncGloboBackInStockToUnavailableMsg();
          requestAnimationFrame(() => this.#syncGloboBackInStockToUnavailableMsg());
        });
      }
      setTimeout(() => this.#syncGloboBackInStockToUnavailableMsg(), 100);
      setTimeout(() => this.#syncGloboBackInStockToUnavailableMsg(), 400);
    }
  }
  customElements.define('configurator-sticky-price-atc', ConfiguratorStickyPriceAtc);
}

if (!customElements.get('cue-builder-product')) {
  class CueBuilderProduct extends HTMLElement {
    connectedCallback() {
      this.attachOptionListeners();
      this._changeHandler = this.updateSelectedProductVariant.bind(this);
      //this.addEventListener('change', this._changeHandler);
      this.section = this.closest('.js-product');
      this.defaultVariantId = this.dataset.defaultVariantId;
      // console.log('cue builder product constructor',this.dataset);
      // console.log('default variant id:',this.defaultVariantId);
      this.updateObjectData();
      //this.logData();
      
      this._observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.type !== 'childList') continue;


          const added   = [...m.addedNodes];
          const removed = [...m.removedNodes];

          const changedRelevant =
            [...added, ...removed].some(
              (n) =>
                n.nodeType === 1 &&
                (n.matches?.('.option-selector') ||
                 n.querySelector?.('.option-selector'))
            );

          if (changedRelevant) {
            this.attachOptionListeners();
            break;
          }
        }
      });

      this._observer.observe(this, {
        childList: true,
        subtree: true
      });
    }

    disconnectedCallback() {
      this._observer?.disconnect();
      this.detachOptionListeners?.();
      if (this._changeHandler) {
        this.removeEventListener('change', this._changeHandler);
      }
    }
    constructor() {
      super();

    }

    /** Option rows used to resolve variant / price (excludes shaft product-switcher UI). */
    getOptionSelectorsForVariantResolution() {
      return this.querySelectorAll('.option-selector:not(.shaft-picker)');
    }

    applyProductIdentityFromOptionSelector(optionSelector) {
      if (!optionSelector?.dataset) return;
      if (optionSelector.dataset.productHandle) {
        this.setAttribute('data-selected-product-handle', optionSelector.dataset.productHandle);
      }
      if (optionSelector.dataset.productTitle) {
        this.setAttribute('data-selected-product-title', optionSelector.dataset.productTitle);
      }
      if (optionSelector.dataset.shortenedProductTitle) {
        this.setAttribute(
          'data-selected-product-shortened-title',
          optionSelector.dataset.shortenedProductTitle
        );
      }
    }

    refreshSummaryAndProductMetadata() {
      this.summaryElement = this.querySelector('summary');
      this.componentType = this.dataset?.componentType;
      this.productHandle = this.dataset.selectedProductHandle;
      if (this.summaryElement && this.productHandle) {
        this.summaryElement.dataset.productHandle = this.productHandle;
      }
      this.productTitle = this.dataset.selectedProductTitle;
      this.productShortenedTitle = this.dataset.selectedProductShortenedTitle;
    }

    /** Raw variant row from JSON for `this.variant`, or null. */
    getRawVariantDataForCurrentVariant() {
      if (!this.variant || !this.data?.[this.productHandle]?.variants) return null;
      return (
        this.data[this.productHandle].variants.find((v) => v.id === this.variant.id) ?? null
      );
    }

    /**
     * Writes `data-selected-variant-*` / price / availability and mirrors the same values on
     * `this` (no re-read from `dataset` — avoids overwriting computed state).
     */
    applyResolvedVariantToHost(rawVariantData) {
      if (this.variant && rawVariantData) {
        const formattedDataForVariant = this.data[this.productHandle].formatted?.[this.variant.id];
        const excludeFromOrder = formattedDataForVariant?.excludeFromOrder === true;
        const variantIdStr = excludeFromOrder ? '' : String(this.variant.id);
        this.setAttribute('data-selected-variant-id', variantIdStr);
        this.setAttribute('data-selected-variant-sku', this.variant.sku || '');
        this.setAttribute('data-component-price', rawVariantData?.price ?? '');
        this.setAttribute('data-available', rawVariantData?.available === true ? 'true' : 'false');
        this.componentPrice = rawVariantData?.price ?? null;
        this.selectedVariant = variantIdStr;
        this.selectedVariantSku = this.variant.sku || '';
      } else {
        this.setAttribute('data-selected-variant-id', '');
        this.setAttribute('data-selected-variant-sku', '');
        this.setAttribute('data-component-price', '');
        this.setAttribute('data-available', 'false');
        this.componentPrice = null;
        this.selectedVariant = '';
        this.selectedVariantSku = '';
      }
    }

    updateObjectData(optionSelector = null) {
      this.applyProductIdentityFromOptionSelector(optionSelector);
      this.refreshSummaryAndProductMetadata();
      this.optionSelectors = this.getOptionSelectorsForVariantResolution();
      this.data = this.getAllProductVariantsData();
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant(this.productHandle);
      const rawVariantData = this.getRawVariantDataForCurrentVariant();
      this.applyResolvedVariantToHost(rawVariantData);
      this.updateAvailability();
      this.dispatchComponentUpdateEvent(rawVariantData);
      scheduleGloboSyncOnConfiguratorSticky();
    }

    /**
     * Dispatches the cue-component-update event.
     * @param {object|null} [rawVariantData] - When passed from `updateObjectData`, avoids a second `variants.find`.
     */
    dispatchComponentUpdateEvent(rawVariantData = null) {
      const raw =
        rawVariantData ??
        (this.variant && this.data?.[this.productHandle]
          ? this.data[this.productHandle].variants.find((v) => v.id === this.variant.id)
          : null);
      const available = this.variant !== null && raw?.available === true;
      this.dispatchEvent(
        new CustomEvent('cue-component-update', {
          bubbles: true,
          detail: {
            componentSelectedVariant: this.variant,
            componentAvailable: available
          }
        })
      );
    }
    attachOptionListeners() {
      this.detachOptionListeners?.();

      this.optionSelectors = this.querySelectorAll(
        '.option-selector'
      );

      // Store “unsubscribe” callbacks so we can clean up later
      const removers = [];

      this.optionSelectors.forEach((el) => {
        const handler = this.updateSelectedProductVariant.bind(this);
        el.addEventListener('change', handler);
        removers.push(() => el.removeEventListener('change', handler));

        // Keyboard: when tabbing onto a different product's option in the product-switcher,
        // treat focus as selection so the selected product updates without requiring Space/Enter.
        if (el.classList.contains('product-switcher')) {
          const focusHandler = (evt) => {
            const input = evt.target?.closest?.('input.js-option');
            if (!input || input.checked) return;
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          };
          el.addEventListener('focusin', focusHandler);
          removers.push(() => el.removeEventListener('focusin', focusHandler));
        }
      });

      // Expose a method the observer and disconnectedCallback can call
      this.detachOptionListeners = () => removers.forEach((off) => off());
    }
    logData() {
      console.log('test cue builder elem: ',this.selectedOptions);
      console.log('selectors for',this,this.optionSelectors);
      console.log('data: ',this.data);
    }

    /**
     * Gets the variant data for a product.
     * @returns {?object}
     */
    getAllProductVariantsData() {
      const dataElements = this.querySelectorAll('[type="application/json"]');
      //console.log('data elems: ',dataElements);
      const mergedObject = Array.from(dataElements).reduce((accumulator, currentObject) => {
        accumulator[currentObject.dataset.productHandle] = JSON.parse(currentObject.textContent);
        return accumulator;
      }, {});
      //console.log('merged object: ',mergedObject)
      return mergedObject;
    }

    updateSelectedProductVariant(evt, variantId = null) {
      //console.log('evt.target.value:',evt.target.value);
      const optionSelector = evt.target.closest('.option-selector');
      const isProductSwitcher = optionSelector.classList.contains('product-switcher');

      if(isProductSwitcher){
        console.log('is a switcher');
        fetch(`/products/${evt.target.value}?variant=${evt.target.dataset.variantId}&sections=component-tooltip,add-on-variant-picker,tip-change-picker`)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.text();
        })
        .then((text) => {
          const jsonData = JSON.parse(text);
          // Snapshot before any section replaces DOM: openers may update first, and multiple
          // product ids can appear in one cue-builder (page product vs selected shaft).
          const tooltipProductIdsBeforeReplace = new Set();
          this.querySelectorAll('modal-opener[data-modal^="tooltip-"]').forEach((op) => {
            const m = op.dataset.modal?.match(/^tooltip-(\d+)-/);
            if (m) tooltipProductIdsBeforeReplace.add(m[1]);
          });
          this.querySelectorAll('modal-dialog.modal--for-product[id^="tooltip-"]').forEach((dlg) => {
            const m = dlg.id?.match(/^tooltip-(\d+)-/);
            if (m) tooltipProductIdsBeforeReplace.add(m[1]);
          });

          for(const [key, value] of Object.entries(jsonData)){
            const selector = (key, handle) => {
              switch(key){
                case 'add-on-variant-picker':
                  return `add-on-variant-picker[data-product-handle="${handle}"]`;
                case 'tip-change-picker':
                  return `cue-builder-product[data-parent-product-handle="${handle}"]`;
                case 'component-tooltip':
                  return `cue-builder-product[data-selected-product-handle="${handle}"] [id^="tooltip-"]`;
                //case 'cue-builder-disclosure-summary':
                // return `summary[data-product-handle="${handle}"]`;
              }
            }
            if(key === 'component-tooltip'){
              const fetchDom = new DOMParser()?.parseFromString(jsonData[key], 'text/html');
              const replacementElements = fetchDom?.querySelectorAll(`modal-dialog`);
              if (replacementElements) {
                // Modals may live under this cue-builder or on document.body (Modal.open reparents
                // .modal--for-product). One cue-builder can reference multiple Shopify product ids
                // (e.g. shaft-type legend uses page `product.id`, option tooltips use selected shaft id).
                const inCueBuilder = Array.from(
                  this.querySelectorAll('modal-dialog.modal--for-product[id^="tooltip-"]')
                );
                const onBody = [];
                if (tooltipProductIdsBeforeReplace.size) {
                  document
                    .querySelectorAll('body > modal-dialog.modal--for-product[id^="tooltip-"]')
                    .forEach((dlg) => {
                      const m = dlg.id?.match(/^tooltip-(\d+)-/);
                      if (m && tooltipProductIdsBeforeReplace.has(m[1])) {
                        onBody.push(dlg);
                      }
                    });
                }
                const seen = new Set();
                const toreplaceElements = [];
                for (const el of [...inCueBuilder, ...onBody]) {
                  if (!seen.has(el)) {
                    seen.add(el);
                    toreplaceElements.push(el);
                  }
                }

                Array.from(replacementElements).forEach((replaceWith) => {
                  const toreplace = toreplaceElements.find(
                    (item) => item.dataset.categoryHandle === replaceWith.dataset.categoryHandle
                  );
                  if (toreplace) {
                    toreplace.replaceWith(replaceWith);
                  }
                });
              }
            }else {
              const toReplace = document.querySelector(selector(key, this.productHandle));
              //console.log('to replace:',toReplace);
              const fetchDom = new DOMParser()?.parseFromString(jsonData[key], 'text/html');
              const replaceWith = fetchDom?.querySelector(selector(key, evt.target.value));
              //console.log('replace with:',replaceWith);
              if (toReplace && replaceWith) {
                toReplace.replaceWith(replaceWith);
                this.setAttribute('data-selected-product-handle',evt.target.value);
                this.setAttribute('data-selected-product-title',evt.target.dataset.productTitle);
                this.setAttribute('data-selected-product-shortened-title',evt.target.dataset.shortenedProductTitle);
                this.attachOptionListeners();
              }
            }
            
          }
          this.updateObjectData();
          this.updateLabelText(evt);
          this.updateSummaryElement();
          scheduleGloboSyncOnConfiguratorSticky();

        })
        .catch((error) => {
          console.warn(error);
          if (error.code === 20) {
            return; // aborted
          }
          throw error;
        });
      }else {
        console.log('not a switcher');

        this.updateObjectData(optionSelector);
        this.updateLabelText(evt);
        this.updateSummaryElement();
        scheduleGloboSyncOnConfiguratorSticky();
      }
    }

    /**
    * Updates the colour option label text.
    * @param {object} evt - Event object
    */
    updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType ==='dropdown') return;
      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;
      const targetLabel = evt.target.nextElementSibling;
      const labelText = targetLabel?.querySelector('span.label-value')?.textContent;
      if(labelText) {
        colorText.textContent = labelText;
      }
    }

    updateSummaryElement(){
      //console.log('updating summary element');
      const summaryProductTitle = this.summaryElement.querySelector('.selected-product__title');
      if (summaryProductTitle && summaryProductTitle.textContent !== this.productTitle) {
        if(this.productShortenedTitle){
          summaryProductTitle.textContent = this.productShortenedTitle;
        }else{
          summaryProductTitle.textContent = this.productTitle;
        }
      }
      if (this.variant) {
        const options = this.getVariantOptions(this.variant);
        if (options.length) {
          const summarySelectedOptionsWrapper = this.summaryElement.querySelector('.configurator-disclosure__selected-options');
          const titleIsDisplayed = this.summaryElement.querySelector('.selected-product__title');
          if (summarySelectedOptionsWrapper) {
            summarySelectedOptionsWrapper.innerHTML =
              `${titleIsDisplayed ? `<span class="selected-product__title">${this.productShortenedTitle ? this.productShortenedTitle : this.productTitle}</span><span> • </span>` : ''}
            <div class="selected-product__options">
              ${options.map((option, index) =>
                `<span data-option-index="${index}">${option}</span>
                ${index === options.length - 1 ? '' : '<span> • </span>'}`
              ).join('')}
            </div>`;
          }
        }
      }
    }
    getSelectedOptions() {
      const selectedOptions = [];
      //console.log('selectors:',this.optionSelectors)
      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptions.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });
      //console.log('selected options:',selectedOptions.filter((option) => option !== null));
      //add comment!
      return selectedOptions.filter((option) => option !== null);
    }

    /**
     * Get options array from a variant (handles Shopify option1/option2/option3 or .options).
     * @param {object} v - Variant object.
     * @returns {string[]} Options array.
     */
    getVariantOptions(v) {
      if (v.options && Array.isArray(v.options)) return v.options;
      return [v.option1, v.option2, v.option3].filter(Boolean);
    }

    /**
     * Get selected variant data for a product.
     * When multiple products exist as fieldsets, uses that product's selected options only.
     * @param {string} handle - Product handle.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant(handle) {
      const productData = this.data[handle];
      if (!productData || !productData.variants) return null;
      const selectedOptionsForHandle = this.getSelectedOptionsForProduct(handle);
      if (!selectedOptionsForHandle.length) return null;
      const selected = productData.variants.find((v) => {
        const opts = this.getVariantOptions(v);
        return opts.length === selectedOptionsForHandle.length &&
          opts.every((val, index) => String(val) === String(selectedOptionsForHandle[index]));
      });
      return selected || null;
    }

    /**
     * Gets selected option values for a single product (current or given handle).
     * Used when multiple products exist as fieldsets and only one product is selected at a time.
     * @param {string} [productHandle] - Product handle; defaults to this.productHandle.
     * @returns {string[]} Selected option values for that product's option selectors, in order.
     */
    getSelectedOptionsForProduct(productHandle = this.productHandle) {
      const selectorsForProduct = Array.from(this.optionSelectors).filter(
        (s) => s.dataset.productHandle === productHandle
      );
      return selectorsForProduct.map((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          return selected && selected.dataset.value !== '' ? selected.dataset.value : null;
        }
        const selected = selector.querySelector('input:checked');
        return selected ? selected.value : null;
      }).filter((val) => val !== null);
    }

    /**
     * Updates the availability status in option selectors.
     * Marks each option input as unavailable if choosing that option would result in
     * a variant combination that is out of stock (no available variant matches).
     * When multiple products exist as fieldsets (e.g. tips), only the currently selected
     * product's selectors and options are considered.
     */
    updateAvailability() {
      const productData = this.data[this.productHandle];
      if (!productData || !productData.variants || !productData.variants.length) return;

      const variants = productData.variants.map((v) => {
        if (Array.isArray(v.options)) return v;
        const options = [v.option1, v.option2, v.option3].filter(Boolean);
        return { ...v, options };
      });

      const selectorsForProduct = Array.from(this.optionSelectors).filter(
        (s) => s.dataset.productHandle === this.productHandle
      );
      const selectedOptionsForProduct = this.getSelectedOptionsForProduct(this.productHandle);

      const currentSelectionIsAvailable = variants.some(
        (v) => v.options && v.options.length === selectedOptionsForProduct.length &&
          v.options.every((opt, i) => String(opt) === String(selectedOptionsForProduct[i])) &&
          v.available === true
      );

      selectorsForProduct.forEach((selector, productOptionIndex) => {
        const optionInputs = selector.querySelectorAll('.js-option');
        optionInputs.forEach((input) => {
          if (input.checked) {
            input.classList.toggle('is-unavailable', !currentSelectionIsAvailable);
            return;
          }
          const combinationToCheck = selectedOptionsForProduct.toSpliced(productOptionIndex, 1, input.value);
          const variantWithValueIsAvailable = variants.some(
            (v) => v.options && v.options.length === combinationToCheck.length &&
              v.options.every((opt, i) => String(opt) === String(combinationToCheck[i])) &&
              v.available === true
          );
          input.classList.toggle('is-unavailable', !variantWithValueIsAvailable);
        });
      });
    }

    /**
     * Resets this cue-builder-product to its default variant.
     * @returns {Promise<boolean>} Promise that resolves to true if reset was successful, false otherwise.
     */
    async resetToDefaultVariant() {
      if (!this.defaultVariantId) {
        console.warn('Cannot reset: missing default variant ID');
        return false;
      }

      // Ensure data is up to date
      this.updateObjectData();

      // First, find which product contains the default variant
      let defaultVariant = null;
      let defaultVariantProductHandle = null;

      // Search through all product data to find which product has the default variant
      for (const [productHandle, productData] of Object.entries(this.data)) {
        if (productData && productData.variants) {
          const variant = productData.variants.find(
            (v) => v.id.toString() === this.defaultVariantId.toString()
          );
          if (variant) {
            defaultVariant = variant;
            defaultVariantProductHandle = productHandle;
            break;
          }
        }
      }

      const defaultOptions = defaultVariant ? this.getVariantOptions(defaultVariant) : [];
      if (!defaultVariant || !defaultOptions.length) {
        console.warn('Cannot reset: default variant not found or missing options');
        return false;
      }

      // Check if we need to reset the product switcher to the product that contains the default variant
      const productSwitcher = this.querySelector('.option-selector.product-switcher');
      
      if (productSwitcher && defaultVariantProductHandle !== this.productHandle) {
        // Find the input for the product that contains the default variant
        const productSwitcherInputs = productSwitcher.querySelectorAll('input.js-option');
        const targetProductInput = Array.from(productSwitcherInputs).find(
          (input) => input.value === defaultVariantProductHandle
        );
        
        if (targetProductInput && !targetProductInput.checked) {
          // Switch to the product that contains the default variant
          targetProductInput.checked = true;
          targetProductInput.dispatchEvent(new Event('change', { bubbles: true }));
          
          // Wait for the async product switch to complete
          await new Promise((resolve) => {
            let attempts = 0;
            const maxAttempts = 40; // 2 seconds max (40 * 50ms)
            
            const checkInterval = setInterval(() => {
              attempts++;
              this.updateObjectData();
              
              if (this.productHandle === defaultVariantProductHandle || attempts >= maxAttempts) {
                clearInterval(checkInterval);
                resolve();
              }
            }, 50);
          });
        }
      }

      // Ensure optionSelectors and data are up to date
      this.updateObjectData();

      // Get option selectors for the product that contains the default variant
      const relevantSelectors = Array.from(this.optionSelectors).filter(
        (selector) => selector.dataset.productHandle === defaultVariantProductHandle
      );

      // Reset each option selector to match the default variant's options
      defaultOptions.forEach((optionValue, index) => {
        if (index >= relevantSelectors.length) return;

        const selector = relevantSelectors[index];

        if (selector.dataset.selectorType === 'dropdown') {
          // Handle dropdown selectors
          const customSelect = selector.querySelector('custom-select');
          if (customSelect) {
            // Find option by data-value attribute (handles special characters)
            const allOptions = customSelect.querySelectorAll('.custom-select__option');
            const optionToSelect = Array.from(allOptions).find(
              (opt) => opt.dataset.value === optionValue
            );
            if (optionToSelect) {
              customSelect.selectOption(optionToSelect);
            }
          }
        } else {
          // Handle radio input selectors
          // Find input by value attribute (handles special characters)
          const allInputs = selector.querySelectorAll('input.js-option');
          const inputToSelect = Array.from(allInputs).find(
            (input) => input.value === optionValue
          );
          if (inputToSelect) {
            inputToSelect.checked = true;
            // Dispatch change event to trigger update logic
            inputToSelect.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });

      // Update object data and summary after a brief delay to allow DOM updates
      await new Promise((resolve) => setTimeout(resolve, 100));
      this.updateObjectData();
      this.updateSummaryElement();

      return true;
    }
  }

  customElements.define('cue-builder-product', CueBuilderProduct);
}

/**
 * Resets all cue-builder-product and base-picker-options elements to their default variants.
 * @returns {Promise<number>} Promise that resolves to the number of elements successfully reset.
 */
const resetAllCueBuilderProducts = async () => {
  let resetCount = 0;

  // Reset all cue-builder-product elements sequentially to avoid race conditions with product switcher fetches
  const cueBuilderProducts = document.querySelectorAll('cue-builder-product');
  for (const element of cueBuilderProducts) {
    if (element.resetToDefaultVariant) {
      const success = await element.resetToDefaultVariant();
      if (success) {
        resetCount++;
      }
    }
  }

  // Reset all base-picker-options elements
  const basePickerOptions = document.querySelectorAll('base-picker-options');
  for (const element of basePickerOptions) {
    if (element.resetToDefault) {
      const success = element.resetToDefault();
      if (success) {
        resetCount++;
      }
    }
  }

  return resetCount;
}

// Expose the function globally
window.resetAllCueBuilderProducts = resetAllCueBuilderProducts;

const configuratorResetDefaultsButton = document.getElementById('configuratorResetDefaults');
if(configuratorResetDefaultsButton){
  configuratorResetDefaultsButton.addEventListener('click', async () => {
    await resetAllCueBuilderProducts();
  });
}

customElements.whenDefined('product-form').then(() => {
  class CustomCueProductForm extends customElements.get('product-form') {
    connectedCallback() {
      super.connectedCallback?.();
      // Server-rendered markup ships the button `disabled` so a native submit can't fire
      // before this class upgrades and attaches handleSubmit(); release that gate now.
      const submitBtn = this.querySelector('.add-to-cart');
      if (submitBtn) {
        // Keep VIP / early-access gated buttons disabled; only release the pre-upgrade gate.
        if (submitBtn.dataset.upcomingDrop !== 'true' && submitBtn.dataset.vipDisabled !== 'true') {
          submitBtn.removeAttribute('disabled');
          if (submitBtn.getAttribute('aria-disabled') === 'true') {
            submitBtn.removeAttribute('aria-disabled');
          }
        } else {
          submitBtn.setAttribute('disabled', 'true');
        }
      }
    }
    async fetchCartDataLayer() {
      const response = await fetch('/cart?view=data-layer');
      const data = await response.json();
      return data;
    }
    constructLineQuantityMap(cartDataLayer) {
      const lineQuantityMap = {};
      cartDataLayer.lineItems.forEach(line => {
        if(line.components){
          line.components.forEach(component => {
            if(!lineQuantityMap[component.componentVariantId]){
              lineQuantityMap[component.componentVariantId] = {
                'cartQuantity': component.componentCartQuantity,
                'inventoryQuantity': component.componentInventoryQuantity,
                'title': component?.componentTitle
              }
            } else {
              lineQuantityMap[component.componentVariantId]['cartQuantity'] += component.componentCartQuantity;
            }
          });
        } else {
          if(!lineQuantityMap[line.lineVariantId]){ 
            lineQuantityMap[line.lineVariantId] = {
              'cartQuantity': line.lineCartQuantity,
              'inventoryQuantity': line.lineVariantInventoryQuantity,
              'title': line?.lineTitle

            }
          } else {
            lineQuantityMap[line.lineVariantId]['cartQuantity'] += line.lineCartQuantity;
          }
        }
      });
      return lineQuantityMap;
    }
    /**
     * Shows/hides the custom cue builder error region and fills it from `error`.
     * @param {false|object} [error=false] - When false, hides and clears dynamic content.
     */
    setCustomErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      const container = this.errorMsg.querySelector('.custom-cue-error-msg');

      if (!error) {
        this.errorMsg.hidden = true;
        resetCustomCueErrorDynamicContent(container);
        return;
      }

      if (!container) return;

      switch (error.type) {
        case 'quantityInCartReached': {
          const header = container.querySelector('.custom-cue-error-msg-header');
          if (header) {
            const titleSpan = ensureChild(
              header,
              '.custom-cue-error-msg-header-title',
              'span',
              'custom-cue-error-msg-header-title'
            );
            titleSpan.textContent = strTrimOr(
              this.dataset.cueErrorQuantityHeader,
              'Maximum quantity reached!'
            );
          }

          const products = Array.isArray(error.products) ? error.products : [];
          const labelStyle = normalizeCueConfiguratorLabelStyle(
            this.dataset.cueErrorQuantityLabelStyle
          );

          if (products.length > 1) {
            const multiMode = normalizeCueMultiMessageMode(this.dataset.cueErrorQuantityBodyMultiMode);
            if (multiMode === 'simplified') {
              container.querySelector('.custom-cue-error-msg-products-list')?.remove();
              const body = ensureChild(
                container,
                '.custom-cue-error-msg-body',
                'p',
                'custom-cue-error-msg-body'
              );
              body.textContent = strTrimOr(
                this.dataset.cueErrorQuantityBodyMultiSimplified,
                'You already have the maximum quantity in your cart for one or more components of this configuration.'
              );
            } else {
              const body = ensureChild(
                container,
                '.custom-cue-error-msg-body',
                'p',
                'custom-cue-error-msg-body'
              );
              body.textContent = strTrimOr(
                this.dataset.cueErrorQuantityBodyMultiVerbose,
                'All remaining stock of the following products are already in your cart.'
              );
              const list = ensureChild(
                container,
                '.custom-cue-error-msg-products-list',
                'ul',
                'custom-cue-error-msg-products-list'
              );
              list.replaceChildren();
              products.forEach((product) => {
                const item = document.createElement('li');
                item.classList.add('custom-cue-error-msg-product-item');
                item.appendChild(
                  createCueConfiguratorLabelElement(product.title || '', labelStyle)
                );
                list.appendChild(item);
              });
            }
          } else if (products.length === 1) {
            container.querySelector('.custom-cue-error-msg-products-list')?.remove();
            const body = ensureChild(
              container,
              '.custom-cue-error-msg-body',
              'p',
              'custom-cue-error-msg-body'
            );
            const tmpl = strTrimOr(
              this.dataset.cueErrorQuantityBodySingle,
              'All remaining stock of [name] is already in your cart.'
            );
            const ph = '[name]';
            const parts = tmpl.split(ph);
            body.replaceChildren();
            if (parts.length === 1) {
              body.append(
                createCueConfiguratorLabelElement(products[0].title || '', labelStyle),
                document.createTextNode(` ${tmpl}`)
              );
            } else {
              parts.forEach((part, i) => {
                if (part) body.appendChild(document.createTextNode(part));
                if (i < parts.length - 1) {
                  body.appendChild(
                    createCueConfiguratorLabelElement(products[0].title || '', labelStyle)
                  );
                }
              });
            }
          } else {
            container.querySelector('.custom-cue-error-msg-products-list')?.remove();
            const body = ensureChild(
              container,
              '.custom-cue-error-msg-body',
              'p',
              'custom-cue-error-msg-body'
            );
            body.textContent = strTrimOr(
              this.dataset.cueErrorQuantityBodyGeneric,
              'Maximum quantity reached for this item.'
            );
          }
          this.errorMsg.hidden = false;
          break;
        }
        default:
          this.errorMsg.hidden = true;
          resetCustomCueErrorDynamicContent(container);
          break;
      }
    }
    
    async handleSubmit(evt) {
      evt.preventDefault();
      evt.stopImmediatePropagation(); 
      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      this.setErrorMsgState();
      const formValid = this.validate();
      if (!formValid) return;
      const formData = new FormData(this.form);
      const sectionsToUpdate = ['page-header', 'cart-drawer'].map((sel) => document.querySelector(sel)).filter((el) => el);
      const sectionIds = sectionsToUpdate.map((el) => el.dataset.sectionId);
      const itemQuantity = formData.get('quantity');
      const mainProductId = formData.get('id');
      const mainProductSku = formData.get('sku');

      // Cue-builder-product add-ons: _componentType from parent's data-component-type
      const cueBuilderAddOns = Array.from(document.querySelectorAll('cue-builder-product'))
        .filter((el) => el.dataset.selectedVariantId && el.dataset.selectedVariantId !== '')
        .map((el) => ({
          id: el.dataset.selectedVariantId,
          sku: el.dataset.selectedVariantSku || '',
          componentType: (el.dataset.componentType || '').trim() || undefined
        }));



      const addOns = [...cueBuilderAddOns];
      const parentItem = {
        'id': parseInt(mainProductId),
        'quantity': parseInt(itemQuantity),
      };
      const bundleId = self.crypto.randomUUID();
      if(addOns.length){
        parentItem['properties'] = {
          '_addOns': addOns.map((item) => item.sku),
          '_bundleId': bundleId
        };
      }
      // Component type from base-picker-options (main product)
      const basePickerOptions = this.querySelector('base-picker-options');
      const baseComponentType = basePickerOptions && (basePickerOptions.dataset.componentType || '').trim() || undefined;
      if (baseComponentType) {
        parentItem['properties'] = parentItem['properties'] || {};
        parentItem['properties']['_componentType'] = baseComponentType;
      }
      const itemsArray = [parentItem];
      addOns.forEach((addOnItem) => {
        const properties = {
          '_bundledWith': mainProductSku,
          '_bundleId': bundleId
        };
        if (addOnItem.componentType) {
          properties['_componentType'] = addOnItem.componentType;
        }
        itemsArray.push({
          'id': parseInt(addOnItem.id),
          'quantity': parseInt(itemQuantity),
          properties
        });
      });
      //console.log('items array', itemsArray);
      const finalFormData = {
        'items': itemsArray,
        'sections': sectionIds.join(','),
        'sections_url': window.location.pathname
      };
      //console.log('test 644 final form data:',finalFormData);
      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sectionIds.join(','));
      
      
      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(finalFormData)
      }

      try {
        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }
        if (data.status){
          console.log('data.status: ', data.status);
          if(data.status === 422){
            const cartDataLayer = await this.fetchCartDataLayer();
            const lineQuantityMap = this.constructLineQuantityMap(cartDataLayer);
            console.log('line qty map: ', lineQuantityMap);
            console.log('final form data: ', finalFormData.items);
            const formItemsPresentInCart = [];
            finalFormData.items.forEach(item => {
              console.log('item: ', item);
              console.log('line quantity map: ', lineQuantityMap[item.id]);
              if(lineQuantityMap[item.id]){
                lineQuantityMap[item.id].cartQuantity >= lineQuantityMap[item.id].inventoryQuantity && formItemsPresentInCart.push(lineQuantityMap[item.id]);
              }
            });

            this.setCustomErrorMsgState({
              type: 'quantityInCartReached',
              products: formItemsPresentInCart
            });
          }
        }

        if (!response.ok) throw new Error(response.status);

        if (document.querySelector('.template-cart')) {
          // Cart page
          const cartForm = document.querySelector('customized-cart-form');
          if (cartForm && cartForm.enableAjaxUpdate) {
            cartForm.refresh();
          }
        } else if (theme.settings.afterAddToCart === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          if (data.sections && sectionsToUpdate.length === Object.keys(data.sections).length) {
            sectionsToUpdate.forEach((el) => {
              el.updateFromCartChange(data.sections[el.dataset.sectionId]);
            });
          } else {
            // Unexpected null section return has been observed
            this.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
          }

          if (theme.settings.afterAddToCart === 'notification') {
            const notification = document.getElementById('AddedNotification').content.firstElementChild.cloneNode(true);
            notification.dataset.productTitle = data.product_title;
            let notificationContainer = document.querySelector('.pageheader--sticky');
            if (!notificationContainer) {
              notificationContainer = document.querySelector('body');
            }
            notificationContainer.appendChild(notification);
          } else if (theme.settings.afterAddToCart === 'drawer') {
            document.querySelector('.js-cart-drawer').open(this.submitBtn);
          }
        }

        //Added to track add to cart events used by configurator 06-17-26
        if (typeof gtag === 'function') {
          const analyticsEl = document.getElementById('cue-product-analytics');
          if (analyticsEl) {
            try {
              const analytics = JSON.parse(analyticsEl.textContent);
              const qty = parseInt(itemQuantity) || 1;
              const variantId = parseInt(mainProductId);

              if (!variantId) throw new Error(`Invalid mainProductId: ${mainProductId}`);

              const selectedVariant = analytics.variants.find((v) => v.id === variantId);

              const basePicker = this.querySelector('base-picker-options');
              const basePrice = basePicker
                ? parseFloat(basePicker.dataset.componentPrice || 0) / 100
                : (selectedVariant?.price ?? 0);

              const totalValue = (
                parseFloat(basePicker?.dataset.componentPrice || 0) +
                Array.from(document.querySelectorAll('cue-builder-product')).reduce((sum, el) => {
                  return sum + parseFloat(el.dataset.componentPrice || 0);
                }, 0)
              ) / 100 * qty;

              const variantTitle = (selectedVariant?.title === 'Default Title' || !selectedVariant?.title)
                ? undefined
                : selectedVariant.title;

              gtag('event', 'add_to_cart', {
                send_to: ['G-KZ4LD9Y400'],
                currency: analytics.currency,
                value: totalValue,
                ecomm_pagetype: 'product',
                ecomm_prodid: [`shopify_ZZ_${analytics.productId}_${variantId}`],
                ecomm_totalvalue: totalValue,
                event_id: `configurator_${bundleId}`,
                items: [{
                  id: `shopify_ZZ_${analytics.productId}_${variantId}`,
                  name: analytics.productTitle,
                  brand: analytics.productVendor,
                  category: analytics.productType,
                  variant: variantTitle,
                  price: basePrice,
                  quantity: qty
                }]
              });

              console.log('[analytics] gtag add_to_cart fired', { totalValue, basePrice, qty });

            } catch (e) {
              console.error('[analytics] gtag add_to_cart failed:', e);
            }
          }
        }

        // The configurator submits items as an array (`{ items: [...] }`), so the
        // response is `{ items: [...], sections: {...} }` - there's no top-level
        // `title`/`variant_id` the way a single-item /cart/add.js request returns.
        // The primary line item added is items[0].
        const primaryItem = data.items && data.items[0];

        this.dispatchEvent(new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: {
            variantId: primaryItem?.variant_id,
            variantTitle: primaryItem?.title
          }
        }));

        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 2000);
      } catch (error) {
        //console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg.textContent
          }
        }));

        // Cart may change despite error
        this.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));

        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.remove('is-loading');
        this.submitBtn.classList.remove('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
      }
    }
    
  }
  customElements.define('custom-cue-product-form', CustomCueProductForm);
});

customElements.whenDefined('variant-picker').then(() => {

  if (!customElements.get('base-picker-options')) {
    class BasePickerOptions extends HTMLElement {
      constructor() {
        super();
        
      }
      connectedCallback() {
        this._variantChangeHandler = (e) => this.handleChange(e);
        // Capture phase on this so we hear any change from descendant inputs (add-on-variant-picker does not dispatch change)
        this.addEventListener('change', this._variantChangeHandler, true);
        this.updateObjectData();
        this.defaultVariantId = this.dataset.defaultVariantId;
        this.componentPrice = this.dataset.componentPrice;
        if (this.variantPicker) {
          this.variantPicker.addEventListener('change', this._variantChangeHandler);
        }
      }
      updateObjectData = () => {
        // Remove listener from old variantPicker if it exists
        if (this.variantPicker && this._variantChangeHandler) {
          this.variantPicker.removeEventListener('change', this._variantChangeHandler);
        }
        // Block uses add-on-variant-picker; main product form uses variant-picker
        this.variantPicker = this.querySelector('variant-picker') || this.querySelector('add-on-variant-picker');
        this.summaryElement = this.querySelector('summary');
        // Add listener to new variantPicker
        if (this.variantPicker && this._variantChangeHandler) {
          this.variantPicker.addEventListener('change', this._variantChangeHandler);
        }
        this.updateVariantData();
      }
      /**
       * Get variant and selectedOptions from either variant-picker (custom element) or add-on-variant-picker (DOM).
       */
      /**
       * Returns normalized variants array (with .options) from the inner picker's data.
       * @returns {{ variants: Array, selectedOptions: Array } | { variants: [], selectedOptions: [] }}
       */
      getVariantsAndSelectedFromPicker() {
        if (!this.variantPicker) return { variants: [], selectedOptions: [] };
        if (this.variantPicker.variant !== undefined && this.variantPicker.selectedOptions !== undefined) {
          let rawData = this.variantPicker.data || (typeof this.variantPicker.getVariantData === 'function' ? this.variantPicker.getVariantData() : null);
          const variantsList = rawData?.variants ?? (rawData && typeof rawData === 'object' ? Object.values(rawData).find((v) => v?.variants)?.variants ?? [] : []);
          const variants = (variantsList || []).map((v) => {
            if (Array.isArray(v?.options)) return v;
            const options = [v.option1, v.option2, v.option3].filter(Boolean);
            return { ...v, options };
          });
          return { variants, selectedOptions: this.variantPicker.selectedOptions || [] };
        }
        const dataEl = this.variantPicker.querySelector('[type="application/json"]');
        const optionSelectors = this.variantPicker.querySelectorAll('.option-selector:not(.option-selector--custom)');
        if (!dataEl || !optionSelectors.length) return { variants: [], selectedOptions: [] };
        let data;
        try {
          data = JSON.parse(dataEl.textContent);
        } catch {
          return { variants: [], selectedOptions: [] };
        }
        const variants = (data.variants || []).map((v) => {
          if (Array.isArray(v.options)) return v;
          const options = [v.option1, v.option2, v.option3].filter(Boolean);
          return { ...v, options };
        });
        const selectedOptions = [];
        optionSelectors.forEach((selector) => {
          if (selector.dataset.selectorType === 'dropdown') {
            const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
            selectedOptions.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
          } else {
            const selected = selector.querySelector('input:checked');
            selectedOptions.push(selected ? selected.value : null);
          }
        });
        return { variants, selectedOptions };
      }
      /**
       * Get variant and selectedOptions from either variant-picker (custom element) or add-on-variant-picker (DOM).
       */
      getVariantAndOptionsFromPicker() {
        const { variants, selectedOptions } = this.getVariantsAndSelectedFromPicker();
        if (!variants.length) return { variant: null, selectedOptions };
        const variant = variants.find((v) =>
          v.options && v.options.every((val, index) => val === selectedOptions[index])
        ) || null;
        return { variant, selectedOptions };
      }
      updateVariantData(){
        if (!this.variantPicker) return;
        const { variant, selectedOptions } = this.getVariantAndOptionsFromPicker();
        this.variant = variant;
        this.selectedOptions = selectedOptions;
        if (!variant) {
          this.dataset.selectedVariantId = '';
          this.dataset.available = 'false';
          this.dispatchEvent(new CustomEvent('cue-component-update', {
            bubbles: true,
            detail: {
              componentSelectedVariant: null,
              componentAvailable: false
            }
          }));
          this.updateAvailability();
          scheduleGloboSyncOnConfiguratorSticky();
          return;
        }
        this.dataset.selectedVariantId = variant.id;
        this.dataset.selectedVariantSku = variant.sku || '';
        this.dataset.componentPrice = variant.price;
        this.dataset.available = variant.available === true ? 'true' : 'false';
        this.componentPrice = variant.price;
        this.dispatchEvent(new CustomEvent('cue-component-update', {
          bubbles: true,
          detail: {
            componentSelectedVariant: variant,
            componentAvailable: variant.available === true
          }
        }));
        this.updateAvailability();
        scheduleGloboSyncOnConfiguratorSticky();
      }
      /**
       * Updates the availability status in option selectors.
       * Marks each option input as unavailable if choosing that option would result in
       * a variant combination that is out of stock (no available variant matches).
       */
      updateAvailability() {
        const { variants, selectedOptions } = this.getVariantsAndSelectedFromPicker();
        if (!variants.length) return;

        const currentSelectionIsAvailable = variants.some(
          (v) => v.options && v.options.length === selectedOptions.length &&
            v.options.every((opt, i) => String(opt) === String(selectedOptions[i])) &&
            v.available === true
        );

        const optionSelectors = this.variantPicker.optionSelectors ||
          this.variantPicker.querySelectorAll('.option-selector:not(.option-selector--custom)');

        Array.from(optionSelectors).forEach((selector, globalIndex) => {
          const optionInputs = selector.querySelectorAll('.js-option');
          optionInputs.forEach((input) => {
            if (input.checked) {
              input.classList.toggle('is-unavailable', !currentSelectionIsAvailable);
              return;
            }
            const combinationToCheck = selectedOptions.toSpliced(globalIndex, 1, input.value);
            const variantWithValueIsAvailable = variants.some(
              (v) => v.options && v.options.length === combinationToCheck.length &&
                v.options.every((opt, i) => String(opt) === String(combinationToCheck[i])) &&
                v.available === true
            );
            input.classList.toggle('is-unavailable', !variantWithValueIsAvailable);
          });
        });
      }

      /**
      * Updates the colour option label text.
      * @param {object} evt - Event object
      */
      updateLabelText(evt) {
        const selector = evt.target.closest('.option-selector');
        if (selector.dataset.selectorType ==='dropdown') return;
        const colorText = selector.querySelector('.js-color-text');
        if (!colorText) return;
        const targetLabel = evt.target.nextElementSibling;
        const labelText = targetLabel?.querySelector('span.label-value')?.textContent;
        if(labelText) {
          colorText.textContent = labelText;
        }
      }

      updateSummaryElement(){
        if (this.variant && this.selectedOptions && Array.isArray(this.selectedOptions)) {

          this.selectedOptions.forEach((option, index) => {
            const optionToUpdate = this.summaryElement.querySelector(`span[data-option-index="${index}"]`);
            if (optionToUpdate) {
              optionToUpdate.textContent = option;
            }
          });
        }
      }
      handleChange = (e) => {
        // Only react to changes from inside this element (our disclosure options)
        if (!this.contains(e.target)) return;
        // Refresh picker reference in case it wasn't in DOM at connect
        this.updateLabelText(e);
        this.updateObjectData();
        this.updateVariantData();
        this.updateSummaryElement();
        this.updateUrl();
      }

      /**
       * Updates the browser URL with the selected variant id (like variant-picker).
       * Uses the inner picker's data-url and data-update-url when present.
       */
      updateUrl() {
        if (this.dataset.updateUrl === 'false') return;
        const updateUrl = this.variantPicker?.dataset.updateUrl === 'true';
        const baseUrl = this.variantPicker?.dataset.url || this.dataset.url;
        if (!updateUrl || !baseUrl || !this.variant) return;
        const url = `${baseUrl.replace(/\?.*$/, '')}?variant=${this.variant.id}`;
        window.history.replaceState({}, '', url);
      }
      /**
       * Resets the variant-picker to its default variant.
       * @returns {boolean} True if reset was successful, false otherwise.
       */
      resetToDefault() {
        if (!this.variantPicker) {
          console.warn('Cannot reset: variant-picker not found');
          return false;
        }

        // Get the default variant ID from the variant-picker's data attribute
        const defaultVariantId = this.defaultVariantId;
        if (!defaultVariantId) {
          console.warn('Cannot reset: missing default variant ID');
          return false;
        }

        // Get variant data (variant-picker has .data/.getVariantData; add-on-variant-picker has JSON script)
        let variantData = this.variantPicker.data || (typeof this.variantPicker.getVariantData === 'function' ? this.variantPicker.getVariantData() : null);
        if (!variantData && this.variantPicker.querySelector) {
          const dataEl = this.variantPicker.querySelector('[type="application/json"]');
          if (dataEl) {
            try {
              variantData = JSON.parse(dataEl.textContent);
              if (variantData.variants) {
                variantData.variants = variantData.variants.map((v) => {
                  if (Array.isArray(v.options)) return v;
                  return { ...v, options: [v.option1, v.option2, v.option3].filter(Boolean) };
                });
              }
            } catch (_) {}
          }
        }
        if (!variantData || !variantData.variants) {
          console.warn('Cannot reset: variant data not found');
          return false;
        }
        //console.log('variant data:',variantData);
        // Find the default variant
        const defaultVariant = variantData.variants.find(
          (v) => v.id.toString() === defaultVariantId.toString()
        );

        if (!defaultVariant || !defaultVariant.options) {
          console.warn('Cannot reset: default variant not found or missing options');
          return false;
        }

        // Get option selectors from the variant-picker
        const optionSelectors = this.variantPicker.optionSelectors || 
          this.variantPicker.querySelectorAll('.option-selector:not(.option-selector--custom)');

        // Reset each option selector to match the default variant's options
        defaultVariant.options.forEach((optionValue, index) => {
          if (index >= optionSelectors.length) return;

          const selector = optionSelectors[index];

          if (selector.dataset.selectorType === 'dropdown') {
            // Handle dropdown selectors
            const customSelect = selector.querySelector('custom-select');
            if (customSelect) {
              // Find option by data-value attribute (handles special characters)
              const allOptions = customSelect.querySelectorAll('.custom-select__option');
              const optionToSelect = Array.from(allOptions).find(
                (opt) => opt.dataset.value === optionValue
              );
              if (optionToSelect) {
                customSelect.selectOption(optionToSelect);
              }
            }
          } else {
            // Handle radio input selectors
            // Find input by value attribute (handles special characters)
            const allInputs = selector.querySelectorAll('input.js-option');
            const inputToSelect = Array.from(allInputs).find(
              (input) => input.value === optionValue
            );
            if (inputToSelect) {
              inputToSelect.checked = true;
              // Dispatch change event to trigger update logic
              inputToSelect.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        });

        // Update variant data and summary after a brief delay to allow DOM updates
        setTimeout(() => {
          this.updateVariantData();
          this.updateSummaryElement();
        }, 50);

        return true;
      }
      disconnectedCallback() {
        this.removeEventListener('change', this._variantChangeHandler, true);
        if (this.variantPicker && this._variantChangeHandler) {
          this.variantPicker.removeEventListener('change', this._variantChangeHandler);
        }
      }

    }
    customElements.define('base-picker-options', BasePickerOptions)
  }
})
